// src/screens/HouseholdChecklistExpress/HouseholdChecklistExpress.tsx
// -----------------------------------------------------------------------------
// Container component: pulls household data, derives tasks, renders progress
// bar, list, and context-menu.
// -----------------------------------------------------------------------------

import type { FC } from "react";
import React, { useCallback, useMemo, useState, useEffect } from "react";

import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import useHousehold from "src/contexts/household-context";
import type {
  Task,
  Household,
  UserDataObj,
  DocumentV2,
} from "src/types/snugtotal";
import { paths } from "src/paths";
import { areChecklistDocumentsComplete } from "src/utils/checklist-completion";
import { toast } from "react-hot-toast";
import {
  GenericPath,
  useV3Update,
  useV3DocumentDownload,
  useV3UploadSignedCopy,
} from "src/api/snugtotal/estate_app";
import { EstateDocumentType, MinimalEstateDocument } from "src/types/snugdocs";
import { useEstateDocumentStore } from "src/hooks/snug/use-estateDocs";
import { useEstateAppApiContext } from "src/contexts/estateAppApi-context";
import { useAssetsStore } from "src/hooks/snug/use-assets";
import { useRouter } from "src/hooks/use-router";
import { useEstateDocumentPolling } from "src/hooks/snug/use-estate-doc-polling";
import CircularProgress from "@mui/material/CircularProgress";
import useUserDataProvider from "src/contexts/userData-context";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { UploadSignedCopyDialog } from "src/sections/dashboard/upload-signed-copy-dialog";
import { useDialog } from "src/hooks/use-dialog";

/* =============================================================================
   Inline helpers
============================================================================= */


/** narrow filter: remove null | undefined */
const notEmpty = <T,>(v: T | null | undefined): v is T => v != null;
/** docs -> has at least one with status === APPROVED */
const hasApproved = (docs?: any[]) =>
  docs?.some(
    (d) =>
      d?.status === "APPROVED" ||
      d?.status === "ANALYSIS_PENDING" ||
      d?.status === "ANALYSIS_COMPLETE"
  );
/** docs -> has at least one with status === IN_PROGRESS */
const hasInProgress = (docs?: any[]) =>
  docs?.some((d) => d?.status === "IN_PROGRESS");
/** docs -> has at least one with status === PENDING or REVIEWABLE (treat REVIEWABLE as generating) */
const hasPending = (docs?: any[]) =>
  docs?.some((d) => d?.status === "PENDING" || d?.status === "REVIEWABLE");

/**
 * Select the "best" document from an array based on status priority.
 * Priority: APPROVED/ANALYSIS_PENDING/ANALYSIS_COMPLETE > PENDING/REVIEWABLE > IN_PROGRESS > first in array
 * This ensures we show documents with subdocuments (APPROVED) instead of empty IN_PROGRESS drafts.
 */
const getBestDocument = (docs?: any[]): any | undefined => {
  if (!docs || docs.length === 0) return undefined;

  // Priority 1: APPROVED or analysis statuses (most likely to have subdocuments)
  const approved = docs.find(
    (d) =>
      d?.status === "APPROVED" ||
      d?.status === "ANALYSIS_PENDING" ||
      d?.status === "ANALYSIS_COMPLETE"
  );
  if (approved) return approved;

  // Priority 2: PENDING or REVIEWABLE
  const pending = docs.find(
    (d) => d?.status === "PENDING" || d?.status === "REVIEWABLE"
  );
  if (pending) return pending;

  // Priority 3: IN_PROGRESS
  const inProgress = docs.find((d) => d?.status === "IN_PROGRESS");
  if (inProgress) return inProgress;

  // Fallback: first document
  return docs[0];
};

// PHASE 2: Mode detection helper
/** Determines if a task should render in download mode (preview/signing UI) vs intake mode (creation UI) */
const isTaskReadyForDownloadMode = (task: Task): boolean => {
  // A task is ready for download mode when:
  // 1. Its status is COMPLETE
  // 2. It has approved documents
  return (
    task.status === "COMPLETE" &&
    (hasApproved(task.filteredEstateDocs) ?? false)
  );
};

// PHASE 5: Package support helpers
interface SubDocument {
  id: string;
  title: string;
  type: string;
  documentV2?: any;
}

/** Helper to get sub-documents for display from actual DocumentV2 records */
const getSubDocuments = (task: Task): SubDocument[] => {
  const documents = task.estateDocument?.documents || [];
  // Return sub-documents based on actual DocumentV2 records from backend
  // Reverse order for trust documents so primary trust document appears last
  const subDocs = documents.map((doc, index) => ({
    id: `doc-${index}`,
    title: doc.document_name,
    type: doc.document_name.toLowerCase().includes("trust")
      ? "trust"
      : doc.document_name.toLowerCase().includes("certificate")
      ? "certificate"
      : doc.document_name.toLowerCase().includes("will")
      ? "will"
      : "deed",
    documentV2: doc,
  }));
  return subDocs.reverse();
};

/** Helper to check if a task is a document package */
const isDocumentPackage = (task: Task): boolean => {
  // Check if this estate document has multiple DocumentV2 records (subdocuments)
  const hasMultipleDocuments =
    (task.estateDocument?.documents?.length ?? 0) > 1;

  // Only trusts have subdocument packages (Rev Trust, Pour Over Will, Cert of Trust)
  const isTrust = task.estateDocument?.type === "TRUST";
  return isTrust && hasMultipleDocuments;
};

/** Helper to extract document index from subdocument ID */
const getDocIndexFromSubDocId = (subDocId: string): number => {
  // SubDocId format: "taskId-doc-0", "taskId-doc-1", etc.
  const parts = subDocId.split("-doc-");
  if (parts.length === 2) {
    const index = parseInt(parts[1], 10);
    return isNaN(index) ? 0 : index;
  }
  return 0;
};

// Helper function to generate avatar colors and initials
function stringToColor(string: string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Generate RGB values with constraints for professional colors
  const r = (hash >> 16) & 0xff;
  const g = (hash >> 8) & 0xff;
  const b = hash & 0xff;

  // Mix with gray to reduce saturation (70% original color, 30% gray)
  const gray = 128;
  const mixedR = Math.floor(r * 1 + gray * 0);
  const mixedG = Math.floor(g * 1 + gray * 0);
  const mixedB = Math.floor(b * 1 + gray * 0);

  // Constrain to professional range (60-180) to avoid extremes
  const constrainedR = Math.min(180, Math.max(60, mixedR));
  const constrainedG = Math.min(180, Math.max(60, mixedG));
  const constrainedB = Math.min(180, Math.max(60, mixedB));

  /* eslint-enable no-bitwise */

  // Convert to hex
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(constrainedR)}${toHex(constrainedG)}${toHex(constrainedB)}`;
}

function stringAvatar(name: string | null) {
  if (!name) {
    return {
      sx: {
        bgcolor: "#FFD700",
        color: "black",
        height: 40,
        width: 40,
      },
      children: "-",
    };
  }

  const initials_array = name.split(" ");

  if (initials_array.length === 1) {
    return {
      sx: {
        bgcolor: stringToColor(name),
        color: "white",
        height: 40,
        width: 40,
      },
      children: `${name[0]}${name[1]}`,
    };
  } else {
    return {
      sx: {
        bgcolor: stringToColor(name),
        color: "white",
        height: 40,
        width: 40,
      },
      children: `${initials_array[0][0]}${initials_array[1][0]}`,
    };
  }
}

// Helper function to generate proper document names
const generateDocumentName = (
  type: EstateDocumentType,
  fullName: string,
  jointTrust: boolean,
  spouseName?: string
): string => {
  const year = new Date().getFullYear();
  const name = fullName?.trim() || "Update Name";

  switch (type) {
    case "TRUST":
      if (jointTrust && spouseName) {
        return `${name} & ${spouseName.trim()} ${year} Revocable Living Trust`;
      }
      return `${name} ${year} Revocable Living Trust`;
    case "WILL":
      return `${name} ${year} Last Will and Testament`;
    case "FINANCIAL_POWER_OF_ATTORNEY":
      return `${name} ${year} Financial Power of Attorney`;
    case "HEALTHCARE_DIRECTIVE":
      return `${name} ${year} Healthcare Directive`;
    default:
      return type;
  }
};

// Helper to format last updated time
const formatLastUpdated = (updatedAt?: Date): string => {
  if (!updatedAt) return "";

  const date = new Date(updatedAt);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = diffInMs / (1000 * 60 * 60);

  if (diffInHours < 24) {
    // Show relative time for less than 24 hours
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      if (diffInMinutes < 1) {
        return "Last updated just now";
      }
      return `Last updated ${diffInMinutes} ${
        diffInMinutes === 1 ? "minute" : "minutes"
      } ago`;
    }
    const hours = Math.floor(diffInHours);
    return `Last updated ${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  } else {
    // Show absolute date for more than 24 hours
    return `Last updated ${date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })}`;
  }
};

// ---------------- helpers ----------------------------------------------------
type PersonRole = "HOH" | "SPOUSE" | "CHILD" | "OTHER";
type TaskPlusRole = Task & { primaryRole: PersonRole };

const roleOf = (
  user: UserDataObj,
  hoh: UserDataObj,
  spouse?: UserDataObj | null
): PersonRole => {
  if (user.ud_id === hoh.ud_id) return "HOH";
  if (spouse && user.ud_id === spouse.ud_id) return "SPOUSE";
  // if your member object carries a role field you can refine this:
  return "CHILD"; // or 'OTHER'
};

// ---------------- buildTasks -------------------------------------------------
const buildTasks = (
  hh?: Household | null,
  userData?: UserDataObj | null,
  assetCounts?: { hohAssets: number; spouseAssets: number }
): TaskPlusRole[] => {
  if (!hh || !hh.head_of_household) return [];

  const hoh = hh.head_of_household;
  const members = hh.household_member_roles ?? [];
  const spouse =
    members.find((m) => m.role === "SPOUSE")?.role_target_user_data ?? null;

  const epf = hoh.estate_plan_foundation as
    | "joint_trust"
    | "single_trust"
    | "two_trusts"
    | "single_will"
    | "two_wills"
    | "attorney"
    | "skip"
    | ""
    | null;

  const tasks: TaskPlusRole[] = [];

  // Get household-level allow flags from client role - these override individual preferences
  const householdAllowsFpoa = hh.household_role?.checklist_display_fpoa ?? true;
  const householdAllowsHcd = hh.household_role?.checklist_display_hcd ?? true;
  const householdAllowsWillsTrusts =
    hh.household_role?.checklist_display_willstrusts ?? true;
  const householdAllowsAssets =
    hh.household_role?.checklist_display_assets ?? true;

  // ---- 1. Will / Trust rows -----------------------------------------------
  // Only add Will/Trust tasks if household allows them
  if (householdAllowsWillsTrusts) {
    if (epf === "joint_trust") {
      const jointTrustDocs = hoh.wills_and_trusts ?? [];
      const jointTrustStatus = hasApproved(jointTrustDocs)
        ? "COMPLETE"
        : hasPending(jointTrustDocs)
        ? "PENDING"
        : hasInProgress(jointTrustDocs)
        ? "IN_PROGRESS"
        : "NOT_STARTED";
      const bestJointTrust = getBestDocument(jointTrustDocs);

      tasks.push({
        id: "jointTrust",
        kind: "lwt",
        title: "Joint Revocable Living Trust",
        people: [hoh, spouse].filter(notEmpty),
        estMinutes: 18,
        status: jointTrustStatus,
        updatedAt: bestJointTrust?.updated_at,
        path: paths.dashboard.lwt(hoh.ud_id!),
        primaryRole: "HOH", // 👈 added
        estateDocument: bestJointTrust,
        ud_id: hoh.ud_id ?? "",
        type: "TRUST",
        joint_trust: true,
        filteredEstateDocs: hoh.wills_and_trusts ?? [],
        included: true, // Wills/Trusts don't have individual include fields
      });
    } else if (epf === "skip" || epf === "" || epf === null) {
      const skipDocs = hoh.wills_and_trusts ?? [];
      const bestSkipDoc = getBestDocument(skipDocs);

      tasks.push({
        id: "skip",
        kind: "lwt",
        title: "Choose a Will or Trust",
        people: [hoh, spouse].filter(notEmpty),
        estMinutes: 5,
        status: "NOT_STARTED",
        updatedAt: bestSkipDoc?.updated_at,
        path: paths.dashboard.lwt(userData?.ud_id!),
        primaryRole: "HOH", // 👈 added
        estateDocument: bestSkipDoc,
        ud_id: hoh.ud_id ?? "",
        type: "WILL",
        joint_trust: false,
        filteredEstateDocs: hoh.wills_and_trusts ?? [],
        included: true, // Wills/Trusts don't have individual include fields
      });
    } else if (epf === "attorney") {
      const attorneyDocs = hoh.wills_and_trusts ?? [];
      const attorneyStatus = hasApproved(attorneyDocs)
        ? "COMPLETE"
        : hasPending(attorneyDocs)
        ? "PENDING"
        : hasInProgress(attorneyDocs)
        ? "IN_PROGRESS"
        : "NOT_STARTED";
      const bestAttorneyDoc = getBestDocument(attorneyDocs);

      tasks.push({
        id: "attorney",
        kind: "lwt",
        title: "Upload Will or Trust from Attorney",
        people: [hoh, spouse].filter(notEmpty),
        estMinutes: 2,
        status: attorneyStatus,
        updatedAt: bestAttorneyDoc?.updated_at,
        path: paths.dashboard.lwt(hoh.ud_id!),
        primaryRole: "HOH", // 👈 added
        estateDocument: bestAttorneyDoc,
        ud_id: hoh.ud_id ?? "",
        type: "WILL",
        joint_trust: false,
        filteredEstateDocs: hoh.wills_and_trusts ?? [],
        included: true, // Wills/Trusts don't have individual include fields
      });
    } else if (
      epf === "single_will" ||
      epf === "two_wills" ||
      epf === "single_trust" ||
      epf === "two_trusts"
    ) {
      // Determine document type and label
      const isTrust = epf === "single_trust" || epf === "two_trusts";
      const label = isTrust ? "Living Trust" : "Last Will & Testament";
      const docType = isTrust ? "TRUST" : "WILL";

      // For single plans, only create task for HOH
      // For two plans (two_wills, two_trusts), create tasks for both HOH and spouse
      const peopleToInclude =
        epf === "single_will" || epf === "single_trust"
          ? [hoh]
          : [hoh, spouse].filter(notEmpty);

      peopleToInclude.forEach((p) => {
        const docs = p.wills_and_trusts ?? [];
        const first = p.full_name?.split(/\s+/)[0] ?? "Member";
        const lwtStatus = hasApproved(docs)
          ? "COMPLETE"
          : hasPending(docs)
          ? "PENDING"
          : hasInProgress(docs)
          ? "IN_PROGRESS"
          : "NOT_STARTED";
        const bestDoc = getBestDocument(docs);

        tasks.push({
          id: `${p.ud_id}-lwt`,
          kind: "lwt",
          title: `${label} for ${first}`,
          type: docType,
          people: [p],
          estMinutes: isTrust ? 10 : 8,
          status: lwtStatus,
          updatedAt: bestDoc?.updated_at,
          path: paths.dashboard.lwt(p.ud_id!),
          primaryRole: roleOf(p, hoh, spouse), // 👈 added
          estateDocument: bestDoc,
          ud_id: p.ud_id ?? "",
          joint_trust: false,
          filteredEstateDocs: docs,
          included: true, // Wills/Trusts don't have individual include fields
        });
      });
    }
  }

  // ---- 2. POA + HCD rows for everyone -------------------------------------
  const everyone: UserDataObj[] = [
    hoh,
    ...members
      .filter((r) => {
        if (r.is_read_only === true) return false;
        return true;
      })
      .map((r) => r.role_target_user_data),
  ].filter(notEmpty);

  everyone.forEach((p) => {
    const first = p.full_name?.split(/\s+/)[0] ?? "Member";
    const r = roleOf(p, hoh, spouse);

    const fpoaDocs = p.financial_powers_of_attorney ?? [];
    const fpoaStatus = hasApproved(fpoaDocs)
      ? "COMPLETE"
      : hasPending(fpoaDocs)
      ? "PENDING"
      : hasInProgress(fpoaDocs)
      ? "IN_PROGRESS"
      : "NOT_STARTED";
    const bestFpoa = getBestDocument(fpoaDocs);

    // Only add FPOA task if household allows it
    if (householdAllowsFpoa) {
      tasks.push({
        id: `${p.ud_id}-fpoa`,
        kind: "fpoa",
        title: `Power of Attorney for ${first}`,
        type: "FINANCIAL_POWER_OF_ATTORNEY",
        people: [p],
        estMinutes: 3,
        status: fpoaStatus,
        updatedAt: bestFpoa?.updated_at,
        path: paths.dashboard.fpoa(p.ud_id!),
        primaryRole: r, // 👈 added
        estateDocument: bestFpoa,
        ud_id: p.ud_id ?? "",
        joint_trust: false,
        filteredEstateDocs: p.financial_powers_of_attorney ?? [],
        included: p.include_fpoa ?? true, // Only check individual setting
      });
    }

    // HCD - Debug logging
    const hcdDocs = p.healthcare_directives ?? [];
    const hcdStatus = hasApproved(hcdDocs)
      ? "COMPLETE"
      : hasPending(hcdDocs)
      ? "PENDING"
      : hasInProgress(hcdDocs)
      ? "IN_PROGRESS"
      : "NOT_STARTED";
    const bestHcd = getBestDocument(hcdDocs);

    // Only add HCD task if household allows it
    if (householdAllowsHcd) {
      tasks.push({
        id: `${p.ud_id}-hcd`,
        kind: "hcd",
        title: `Health Care Directive for ${first}`,
        type: "HEALTHCARE_DIRECTIVE",
        people: [p],
        estMinutes: 2,
        status: hcdStatus,
        updatedAt: bestHcd?.updated_at,
        path: paths.dashboard.hcd(p.ud_id!),
        primaryRole: r, // 👈 added
        estateDocument: bestHcd,
        ud_id: p.ud_id ?? "",
        joint_trust: false,
        filteredEstateDocs: p.healthcare_directives ?? [],
        included: p.include_hcd ?? true, // Only check individual setting
      });
    }
  });

  // ---- 4. Assets Planning -----------------------------------------------
  // Add assets task(s) based on household structure
  if (householdAllowsAssets) {
    // Determine if this is a couple or single account
    const hasCoupleStructure = spouse !== null;

    if (hasCoupleStructure) {
      // JOINT ASSETS for couples (joint trust, two wills, two trusts, etc.)
      // This will render in the joint section below all joint documents
      const totalAssets = (assetCounts?.hohAssets ?? 0) + (assetCounts?.spouseAssets ?? 0);
      const jointAssetsStatus = totalAssets > 0 ? "COMPLETE" : "NOT_STARTED";
      // Only set label when there are assets (to show count instead of "Complete")
      const jointAssetsLabel = totalAssets > 0
        ? (totalAssets === 1 ? "1 Asset" : `${totalAssets} Assets`)
        : undefined;

      tasks.push({
        id: "joint-assets",
        kind: "assets",
        title: "Asset Planning",
        type: "ASSETS",
        people: [hoh, spouse].filter(notEmpty), // Multiple people = joint section
        estMinutes: 5,
        status: jointAssetsStatus,
        label: jointAssetsLabel,
        updatedAt: undefined,
        path: paths.dashboard.assetsExpress(hoh.ud_id ?? ""),
        primaryRole: "HOH",
        estateDocument: undefined,
        ud_id: hoh.ud_id ?? "",
        joint_trust: false,
        filteredEstateDocs: [],
        included: hoh.include_asset_planning ?? true,
      });
    } else {
      // INDIVIDUAL ASSETS for single accounts
      // This will render in HOH's individual section
      const hohAssetCount = assetCounts?.hohAssets ?? 0;
      const hohAssetsStatus = hohAssetCount > 0 ? "COMPLETE" : "NOT_STARTED";
      // Only set label when there are assets (to show count instead of "Complete")
      const hohAssetsLabel = hohAssetCount > 0
        ? (hohAssetCount === 1 ? "1 Asset" : `${hohAssetCount} Assets`)
        : undefined;

      tasks.push({
        id: `${hoh.ud_id}-assets`,
        kind: "assets",
        title: "Asset Planning",
        type: "ASSETS",
        people: [hoh], // Single person = individual section
        estMinutes: 5,
        status: hohAssetsStatus,
        label: hohAssetsLabel,
        updatedAt: undefined,
        path: paths.dashboard.assetsExpress(hoh.ud_id ?? ""),
        primaryRole: "HOH",
        estateDocument: undefined,
        ud_id: hoh.ud_id ?? "",
        joint_trust: false,
        filteredEstateDocs: [],
        included: hoh.include_asset_planning ?? true,
      });
    }
  }

  return tasks;
};

/* ---------------------------------------------------------------------------
   Sort tasks by (1) person-role priority, then (2) completion status, then
   (3) kind ('lwt', 'fpoa', 'hcd') for a stable, readable list.
--------------------------------------------------------------------------- */

const rolePriority: Record<PersonRole, number> = {
  HOH: 0,
  SPOUSE: 1,
  CHILD: 2,
  OTHER: 3,
};

const statusPriority: Record<Task["status"], number> = {
  NOT_STARTED: 0,
  IN_PROGRESS: 0,
  PENDING: 0,
  COMPLETE: 0,
  REVIEWABLE: 0,
};

const kindOrder: Record<Task["kind"], number> = {
  // tie-breaker
  lwt: 0,
  fpoa: 1,
  hcd: 2,
  assets: 3,
};

const sortTasks = (tasks: TaskPlusRole[]): TaskPlusRole[] =>
  [...tasks].sort((a, b) => {
    // 1️⃣  status: completed items always last
    const byStatus = statusPriority[a.status] - statusPriority[b.status]; // 0,1,2
    if (byStatus !== 0) return byStatus;

    // 2️⃣  same status → sort by household role
    const byRole = rolePriority[a.primaryRole] - rolePriority[b.primaryRole];
    if (byRole !== 0) return byRole;

    // 3️⃣  tie-breaker: document kind
    return kindOrder[a.kind] - kindOrder[b.kind];
  });

const HouseholdChecklistExpress: FC<{
  readOnly: boolean;
  express: boolean;
  triggerConfetti?: (source?: "checklist" | "download") => void;
  setViewMode?: (mode: "intake" | "download") => void;
  useIndexPaths?: boolean;
}> = ({
  readOnly,
  express,
  triggerConfetti,
  setViewMode,
  useIndexPaths = false,
}) => {
  // ---------------------------------------------------------------------------
  // 1. source data → flat task array
  // ---------------------------------------------------------------------------
  const { selectedHousehold, refreshHouseholds } = useHousehold();
  const { userData } = useUserDataProvider();
  const allowClientDocUpdates = selectedHousehold?.professional_group?.allow_client_doc_updates ?? true;
  const estateDocStore = useEstateDocumentStore(null);
  const { estateAppApiInstance } = useEstateAppApiContext();

  // Fetch assets for HOH and spouse to show counts on checklist
  const hohUdId = selectedHousehold?.head_of_household?.ud_id;
  const spouseUdId = selectedHousehold?.household_member_roles
    ?.find((m) => m.role === "SPOUSE")
    ?.role_target_user_data?.ud_id;

  const { assets: hohAssets } = useAssetsStore(hohUdId || null);
  const { assets: spouseAssets } = useAssetsStore(spouseUdId || null);

  const assetCounts = useMemo(
    () => ({
      hohAssets: hohAssets?.length ?? 0,
      spouseAssets: spouseAssets?.length ?? 0,
    }),
    [hohAssets, spouseAssets]
  );

  const tasks = useMemo(
    () => sortTasks(buildTasks(selectedHousehold, userData, assetCounts)),
    [selectedHousehold, userData, assetCounts]
  );

  // Get estate document handlers for payment dialog and upload
  const {
    estateDocumentSaving,
    estateDocFilesToUpload,
    handleEstateDocumentFilesDrop,
    handleEstateDocumentFileRemove,
    handleEstateDocumentFilesRemoveAll,
    handleEdiEstateDocumentPatchOnly,
  } = useEstateDocumentStore(userData?.ud_id || null, undefined);

  // Create upload signed copy function that can use any ud_id (not just HOH)
  const uploadSignedCopyApi = useV3UploadSignedCopy<DocumentV2>();

  // Wrapper function that accepts personUdId to upload with the correct user
  const handleUploadSignedCopyWithPersonUdId = useCallback(
    async (personUdId: string, documentId: string, file: File) => {
      if (!personUdId || !documentId) {
        toast.error("Missing required information");
        return null;
      }

      try {
        toast.success("Uploading signed copy...");
        const response = await uploadSignedCopyApi(
          personUdId,
          documentId,
          file
        );
        return response;
      } catch (error) {
        toast.error("Failed to upload signed copy");
        console.error("Upload signed copy error:", error);
        return null;
      }
    },
    [uploadSignedCopyApi]
  );

  // State for selected documents
  const [selectedDocs, setSelectedDocs] = useState<Record<string, boolean>>({});

  // State for tracking which switches are loading
  const [loadingSwitches, setLoadingSwitches] = useState<
    Record<string, boolean>
  >({});

  // State for tracking which document rows are being created/loaded
  const [loadingTasks, setLoadingTasks] = useState<Record<string, boolean>>({});

  // State for remove document confirmation dialog
  const [removeDocDialogOpen, setRemoveDocDialogOpen] = useState(false);
  const [pendingRemoveTask, setPendingRemoveTask] = useState<Task | null>(null);

  // State for continue button loading
  const [continueButtonLoading, setContinueButtonLoading] = useState(false);

  // State for completed document dialog
  const [completedDialogOpen, setCompletedDialogOpen] = useState(false);
  const [pendingCompletedTask, setPendingCompletedTask] = useState<Task | null>(
    null
  );

  // PHASE 1: Download feature state additions
  // State for signed documents (tracks which docs have been signed)
  const [signedDocs, setSignedDocs] = useState<Record<string, boolean>>({});

  // State for PDF preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [makeChangesLoading, setMakeChangesLoading] = useState(false);

  // State for context menus
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [menuTask, setMenuTask] = useState<{
    task: Task;
    docId: string;
  } | null>(null);
  const [signatureMenuAnchorEl, setSignatureMenuAnchorEl] =
    useState<HTMLElement | null>(null);
  const [signatureMenuTask, setSignatureMenuTask] = useState<{
    task: Task;
    docId: string;
  } | null>(null);

  // Upload signed copy dialog state
  const uploadSignedCopyDialog = useDialog<{
    task: Task;
    documentId: string;
    personUdId: string;
  }>();

  // Handler to confirm and remove a document
  const handleConfirmRemoveDoc = async () => {
    if (!pendingRemoveTask) return;

    const task = pendingRemoveTask;
    const newValue = false; // removing the document

    // Update local state immediately for responsive UI
    setSelectedDocs((prev) => ({ ...prev, [task.id]: newValue }));

    // Only update DB for FPOA, HCD, and Assets tasks (not LWT)
    if (task.kind === "fpoa" || task.kind === "hcd" || task.kind === "assets") {
      try {
        // Set loading state
        setLoadingSwitches((prev) => ({ ...prev, [task.id]: true }));

        // Get the person's UserData
        const person = task.people[0];

        if (person?.ud_id && userData?.ud_id) {
          // Determine which field to update
          const field =
            task.kind === "fpoa"
              ? "include_fpoa"
              : task.kind === "hcd"
              ? "include_hcd"
              : "include_asset_planning";

          // Call API to update UserData
          await updateUserData(
            {
              ud_id: userData.ud_id,
              entity: "role-target-user-data",
              entity_id: person.ud_id,
            },
            { [field]: newValue },
            true // partial update
          );

          toast.success("Removed document from checklist");
        } else {
          toast.success("Removed document from checklist");
        }
      } catch (error) {
        console.error("Failed to update document preference:", error);
        // Revert local state on error
        setSelectedDocs((prev) => ({ ...prev, [task.id]: true }));
        toast.error("Failed to save preference. Please try again.");
      } finally {
        // Clear loading state
        setLoadingSwitches((prev) => ({ ...prev, [task.id]: false }));
      }
    } else {
      toast.success("Removed document from checklist");
    }

    // Close dialog and clear pending task
    setRemoveDocDialogOpen(false);
    setPendingRemoveTask(null);
  };

  // Separate joint tasks from individual tasks
  const { jointTasks, tasksByPerson } = useMemo(() => {
    const joint: Task[] = [];
    const grouped: Record<string, { person: UserDataObj; tasks: Task[] }> = {};

    tasks.forEach((task) => {
      // Joint tasks have multiple people (e.g., joint trust with HOH + spouse)
      if (task.people.length > 1) {
        joint.push(task);
      } else {
        // Individual tasks - group by person
        const person = task.people[0];
        const id = person.ud_id || "";
        if (!grouped[id]) {
          grouped[id] = { person, tasks: [] };
        }
        grouped[id].tasks.push(task);
      }
    });

    return { jointTasks: joint, tasksByPerson: grouped };
  }, [tasks]);

  // Initialize selected docs state
  useEffect(() => {
    const initialSelected: Record<string, boolean> = {};
    tasks.forEach((task) => {
      // Read from task.included (which comes from UserData) or default to true
      initialSelected[task.id] = task.included ?? true;
    });
    setSelectedDocs(initialSelected);
  }, [selectedHousehold?.head_of_household?.ud_id, tasks]); // Only re-initialize when household changes, not when tasks change

  // ---------------------------------------------------------------------------
  // 2. progress bar
  // ---------------------------------------------------------------------------
  // PHASE 6: Progress calculation includes both creation AND signing
  // Only count selected (non-skipped) tasks in the completion calculation
  // EXCLUDE assets tasks from completion calculations (they're optional)
  const selectedTasks = tasks.filter(
    (t) => selectedDocs[t.id] !== false && t.kind !== "assets"
  );

  // Count completed tasks (created documents)
  const completedTasks = selectedTasks.filter(
    (t) => t.status === "COMPLETE"
  ).length;

  // Count signed documents across all completed tasks
  const signedCount = selectedTasks.reduce((count, task) => {
    if (task.status !== "COMPLETE") return count;

    // For packages, check each sub-document
    if (isDocumentPackage(task)) {
      const subDocs = getSubDocuments(task);
      return (
        count +
        subDocs.filter((subDoc) => {
          const subDocId = `${task.id}-${subDoc.id}`;
          return signedDocs[subDocId] || false;
        }).length
      );
    }

    // For regular documents, check if signed
    return count + (signedDocs[task.id] ? 1 : 0);
  }, 0);

  // Total progress points: each task contributes 2 points (1 for creation, 1 for signing)
  const totalProgressPoints = selectedTasks.length * 2;
  const currentProgressPoints = completedTasks + signedCount;
  const completionPct =
    totalProgressPoints > 0
      ? Math.round((currentProgressPoints / totalProgressPoints) * 100)
      : 0;

  // Check if any documents have been started (excluding assets)
  const hasStartedDocuments = tasks.some(
    (t) =>
      (t.status === "COMPLETE" || t.status === "IN_PROGRESS") &&
      t.kind !== "assets"
  );

  // Get completed tasks for preview navigation
  const completedTasksList = useMemo(() => {
    return selectedTasks.filter((task) => task.status === "COMPLETE");
  }, [selectedTasks]);

  // Get all previewable documents (flattened list including sub-documents)
  const allPreviewableDocuments = useMemo(() => {
    interface PreviewableDocument {
      id: string;
      title: string;
      parentTask: Task;
      subDocument?: SubDocument;
    }

    const documents: PreviewableDocument[] = [];

    completedTasksList.forEach((task) => {
      if (isDocumentPackage(task)) {
        // For packages, add each sub-document as a separate previewable item
        const subDocs = getSubDocuments(task);
        subDocs.forEach((subDoc) => {
          documents.push({
            id: `${task.id}-${subDoc.id}`,
            title: subDoc.title,
            parentTask: task,
            subDocument: subDoc,
          });
        });
      } else {
        // For regular documents, add as-is
        documents.push({
          id: task.id,
          title: task.title,
          parentTask: task,
        });
      }
    });

    return documents;
  }, [completedTasksList]);

  // Collect all estate documents from the household WITH their owner's ud_id
  const allEstateDocumentsWithOwner = useMemo(() => {
    if (!selectedHousehold) return [];

    const docs: Array<{ doc: MinimalEstateDocument; owner_ud_id: string }> = [];

    // HOH documents
    if (selectedHousehold.head_of_household) {
      const hoh = selectedHousehold.head_of_household;
      const hohUdId = hoh.ud_id || "";
      (hoh.wills_and_trusts || []).forEach((doc) =>
        docs.push({ doc, owner_ud_id: hohUdId })
      );
      (hoh.financial_powers_of_attorney || []).forEach((doc) =>
        docs.push({ doc, owner_ud_id: hohUdId })
      );
      (hoh.healthcare_directives || []).forEach((doc) =>
        docs.push({ doc, owner_ud_id: hohUdId })
      );
    }

    // Household member documents
    selectedHousehold.household_member_roles?.forEach((role) => {
      const member = role.role_target_user_data;
      if (member && member.ud_id) {
        const memberUdId = member.ud_id;
        (member.wills_and_trusts || []).forEach((doc) =>
          docs.push({ doc, owner_ud_id: memberUdId })
        );
        (member.financial_powers_of_attorney || []).forEach((doc) =>
          docs.push({ doc, owner_ud_id: memberUdId })
        );
        (member.healthcare_directives || []).forEach((doc) =>
          docs.push({ doc, owner_ud_id: memberUdId })
        );
      }
    });

    return docs;
  }, [selectedHousehold]);

  // Set up polling for PENDING documents
  useEstateDocumentPolling({
    estateDocuments: allEstateDocumentsWithOwner,
    onDocumentUpdated: (updatedDoc) => {
      // When a document finishes generating, refresh the household data
      refreshHouseholds();
    },
  });

  // Track if we've already triggered confetti to avoid multiple triggers
  const [hasTriggeredConfetti, setHasTriggeredConfetti] = useState(false);

  // Trigger confetti and congratulations dialog when completion reaches 100%
  useEffect(() => {
    // Check if all completed documents are also signed
    // If they are, skip the "created" toast - the "signed" toast will show in download view
    const allDocumentsSigned = tasks
      .filter((task) => task.status === "COMPLETE")
      .every((task) => {
        // Use task.estateDocument?.documents which contains the actual document data with is_signed
        const docs = task.estateDocument?.documents || [];
        // Check if all documents for this task have is_signed === true
        return docs.length > 0 && docs.every((doc) => doc.is_signed === true);
      });

    // Validate that DB state also shows completion (prevents edge cases where UI is ahead of DB)
    const dbStateComplete = areChecklistDocumentsComplete(selectedHousehold);

    // Only trigger confetti if:
    // 1. Completion is 100%
    // 2. There are selected tasks
    // 3. At least one document is actually completed (not just all skipped)
    // 4. Haven't already triggered confetti
    // 5. triggerConfetti function exists
    // 6. NOT all documents are already signed (prevents duplicate toast with download view)
    // 7. DB state also confirms completion (ensures consistency between UI and database)
    if (
      completionPct === 100 &&
      selectedTasks.length > 0 &&
      completedTasks > 0 &&
      !hasTriggeredConfetti &&
      triggerConfetti &&
      !allDocumentsSigned &&
      dbStateComplete
    ) {
      triggerConfetti("checklist");
      setHasTriggeredConfetti(true);
    } else if (completionPct < 100 || completedTasks === 0) {
      // Reset the flag if completion drops below 100% or no documents are actually completed
      setHasTriggeredConfetti(false);
      // PHASE 7: No longer switching views - rows handle their own mode
    }
  }, [
    completionPct,
    selectedTasks.length,
    completedTasks,
    hasTriggeredConfetti,
    triggerConfetti,
    selectedDocs,
    tasks,
    selectedHousehold,
    signedDocs,
  ]);

  // Find the next incomplete document (skip PENDING/REVIEWABLE generating documents and assets)
  const nextIncompleteTask = tasks.find(
    (t) =>
      t.status !== "COMPLETE" &&
      t.status !== "PENDING" &&
      t.status !== "REVIEWABLE" &&
      selectedDocs[t.id] !== false &&
      t.kind !== "assets" // Skip assets - they're optional and not part of checklist flow
  );
  const nextPersonFirstName =
    nextIncompleteTask?.people?.[0]?.full_name?.split(/\s+/)[0] || "";
  const nextDocumentTitle = nextIncompleteTask
    ? nextIncompleteTask.kind === "lwt"
      ? nextIncompleteTask.title.includes("Joint")
        ? "Living Trust"
        : nextIncompleteTask.title.includes("Living Trust")
        ? "Living Trust"
        : nextIncompleteTask.title.includes("Will")
        ? "Last Will & Testament"
        : nextIncompleteTask.title.includes("Choose")
        ? "Living Trust"
        : nextIncompleteTask.title.includes("Upload")
        ? "Living Trust"
        : nextIncompleteTask.title
      : nextIncompleteTask.kind === "fpoa"
      ? "Financial Power of Attorney"
      : nextIncompleteTask.kind === "hcd"
      ? "Health Care Directive"
      : nextIncompleteTask.title
    : "";
  const nextDocumentWithPerson =
    nextDocumentTitle && nextPersonFirstName
      ? `${nextDocumentTitle} for ${nextPersonFirstName}`
      : nextDocumentTitle;

  // ---------------------------------------------------------------------------
  // 3. context-menu state
  // ---------------------------------------------------------------------------
  const router = useRouter();

  const updateUserData = useV3Update<UserDataObj>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );

  // PHASE 1: Download feature API hooks
  const downloadDocument = useV3DocumentDownload(
    GenericPath.GENERIC_DOCUMENT_DOWNLOAD_V3
  );

  const updateDocument = useV3Update<any>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );

  // Download all household documents as a single ZIP from backend API
  const handleDownloadAllHouseholdDocuments = useCallback(async () => {
    const completedTasks = tasks.filter(
      (task) =>
        task.status === "COMPLETE" &&
        selectedDocs[task.id] !== false &&
        task.estateDocument?.id &&
        task.ud_id
    );

    if (completedTasks.length === 0) {
      toast.error("No documents available to download");
      return;
    }

    try {
      toast.loading("Preparing documents...");

      // Get the first ud_id (all tasks should have the same ud_id for a household)
      const ud_id = completedTasks[0].ud_id;

      // Collect all estate document IDs
      const estateDocumentIds = completedTasks
        .map((task) => task.estateDocument?.id)
        .filter((id) => id !== undefined);

      if (estateDocumentIds.length === 0) {
        toast.dismiss();
        toast.error("No estate documents found");
        return;
      }

      // Call new backend API to get single ZIP
      const response = await estateAppApiInstance.post(
        `/v3/${ud_id}/estate-documents/download-all-household`,
        { estate_document_ids: estateDocumentIds },
        { responseType: "blob" }
      );

      // Trigger browser download
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Estate Planning Documents.zip";
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      URL.revokeObjectURL(url);

      toast.dismiss();
      toast.success("All documents downloaded");
    } catch (error) {
      console.error("Download failed:", error);
      toast.dismiss();
      toast.error("Failed to download documents");
    }
  }, [tasks, selectedDocs, estateAppApiInstance]);

  // PHASE 1: Download feature handlers
  // Handle PDF preview with optional document ID for sub-documents
  const handlePreview = useCallback(
    async (task: Task, docIndex: number = 0) => {
      if (!task.estateDocument?.id || !task.ud_id) {
        toast.error("No document available for preview");
        return;
      }

      // Find the index in the flattened document list
      // For packages, construct the subdocument ID; for regular docs, use task.id
      const searchId = isDocumentPackage(task)
        ? `${task.id}-doc-${docIndex}`
        : task.id;
      const index = allPreviewableDocuments.findIndex(
        (doc) => doc.id === searchId
      );

      if (index === -1) {
        toast.error("Document not found in preview list");
        return;
      }

      setCurrentPreviewIndex(index);

      setPreviewLoading(true);
      setPreviewTitle(allPreviewableDocuments[index].title);
      setPreviewOpen(true);

      try {
        // Get the DocumentV2 object at the specified index
        const documentV2 = task.estateDocument?.documents?.[docIndex];

        if (!documentV2?.document_id) {
          toast.error("Document ID not found");
          setPreviewOpen(false);
          return;
        }

        // Call the backend API to get a signed URL for preview
        const params = {
          ud_id: task.ud_id,
          document_id: documentV2.document_id,
          entity_id: task.estateDocument.id,
          entity: "estate-documents",
        };

        const response: any = await downloadDocument(params);

        // The API returns the URL directly as a string or as an object with file_url
        if (response && typeof response === "string") {
          setPreviewUrl(response);
        } else if (response && response.file_url) {
          setPreviewUrl(response.file_url);
        } else {
          throw new Error("No file URL returned from API");
        }
      } catch (error) {
        toast.error("Failed to load document preview");
        console.error("Preview error:", error);
        setPreviewOpen(false);
      } finally {
        setPreviewLoading(false);
      }
    },
    [allPreviewableDocuments, downloadDocument]
  );

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewUrl(null);
    setPreviewTitle("");
  };

  // Navigate to previous document
  const handlePreviousDocument = useCallback(async () => {
    if (currentPreviewIndex > 0) {
      const prevDoc = allPreviewableDocuments[currentPreviewIndex - 1];
      setCurrentPreviewIndex(currentPreviewIndex - 1);
      setPreviewTitle(prevDoc.title);
      setPreviewLoading(true);

      try {
        // Get the DocumentV2 object for the previous document
        let documentV2: any = null;
        if (prevDoc.subDocument) {
          documentV2 = prevDoc.subDocument.documentV2;
        } else {
          documentV2 = prevDoc.parentTask.estateDocument?.documents?.[0];
        }

        if (documentV2?.document_id) {
          // Call the backend API to get a signed URL
          const params = {
            ud_id: prevDoc.parentTask.ud_id,
            document_id: documentV2.document_id,
            entity_id: prevDoc.parentTask.estateDocument?.id ?? undefined,
            entity: "estate-documents",
          };

          const response: any = await downloadDocument(params);

          if (response && typeof response === "string") {
            setPreviewUrl(response);
          } else if (response && response.file_url) {
            setPreviewUrl(response.file_url);
          }
        }
      } catch (error) {
        console.error("Error loading previous document:", error);
        toast.error("Failed to load previous document");
      } finally {
        setPreviewLoading(false);
      }
    }
  }, [currentPreviewIndex, allPreviewableDocuments, downloadDocument]);

  // Navigate to next document
  const handleNextDocument = useCallback(async () => {
    if (currentPreviewIndex < allPreviewableDocuments.length - 1) {
      const nextDoc = allPreviewableDocuments[currentPreviewIndex + 1];
      setCurrentPreviewIndex(currentPreviewIndex + 1);
      setPreviewTitle(nextDoc.title);

      // Load the actual DocumentV2 for the next document
      let documentV2: any = null;
      if (nextDoc.subDocument) {
        documentV2 = nextDoc.subDocument.documentV2;
      } else {
        documentV2 = nextDoc.parentTask.estateDocument?.documents?.[0];
      }

      if (!documentV2?.document_id) {
        toast.error("Document ID not found for next document");
        return;
      }

      try {
        setPreviewLoading(true);

        // Call the backend API to get a signed URL for preview
        const params = {
          ud_id: nextDoc.parentTask.ud_id,
          document_id: documentV2.document_id,
          entity_id: nextDoc.parentTask.estateDocument?.id ?? undefined,
          entity: "estate-documents",
        };

        const response: any = await downloadDocument(params);

        // The API returns the URL directly as a string or as an object with file_url
        if (response && typeof response === "string") {
          setPreviewUrl(response);
        } else if (response && response.file_url) {
          setPreviewUrl(response.file_url);
        } else {
          throw new Error("No file URL returned from API");
        }
      } catch (error) {
        console.error("Error loading next document:", error);
        toast.error("Failed to load next document");
      } finally {
        setPreviewLoading(false);
      }
    }
  }, [currentPreviewIndex, allPreviewableDocuments, downloadDocument]);

  // Handle "Make Changes" button - duplicates current document and navigates to express builder
  const handleMakeChanges = async () => {
    const currentDoc = allPreviewableDocuments[currentPreviewIndex];
    if (
      !currentDoc ||
      !currentDoc.parentTask.estateDocument?.id ||
      !currentDoc.parentTask.ud_id
    ) {
      toast.error("Unable to create a copy of this document");
      return;
    }

    try {
      setMakeChangesLoading(true);

      // Duplicate the APPROVED document
      const newEstateDoc = await estateDocStore.duplicateEstateDocument(
        currentDoc.parentTask.ud_id,
        currentDoc.parentTask.estateDocument.id
      );

      if (!newEstateDoc?.id) {
        toast.error("Failed to create document copy");
        return;
      }

      // Navigate to express or standard builder based on document type and express prop
      const task = currentDoc.parentTask;
      switch (task.kind) {
        case "lwt":
          router.push(
            express
              ? paths.dashboard.createWillOrTrustExpress(
                  newEstateDoc.id,
                  task.ud_id
                )
              : paths.dashboard.createWillOrTrust(
                  newEstateDoc.id,
                  task.ud_id
                )
          );
          break;
        case "fpoa":
          router.push(
            express
              ? paths.dashboard.createFinancialPowerOfAttorneyExpress(
                  newEstateDoc.id,
                  task.ud_id
                )
              : paths.dashboard.createFinancialPowerOfAttorney(
                  newEstateDoc.id,
                  task.ud_id
                )
          );
          break;
        case "hcd":
          router.push(
            express
              ? paths.dashboard.createHealthCareDirectiveExpress(
                  newEstateDoc.id,
                  task.ud_id
                )
              : paths.dashboard.createHealthCareDirective(
                  newEstateDoc.id,
                  task.ud_id
                )
          );
          break;
        default:
          toast.error("Unknown document type");
      }
    } catch (error) {
      console.error("Error duplicating document:", error);
      toast.error("Failed to create document copy. Please try again.");
    } finally {
      setMakeChangesLoading(false);
    }
  };

  // Handle menu operations
  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    task: Task,
    docId?: string
  ) => {
    event.stopPropagation();
    const resolvedDocId = docId || task.id;
    // const index = getDocIndexFromSubDocId(resolvedDocId);
    setAnchorEl(event.currentTarget);
    setMenuTask({ task, docId: resolvedDocId });
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuTask(null);
  };

  const handleOpenSignatureMenu = (
    event: React.MouseEvent<HTMLElement>,
    task: Task,
    docId: string
  ) => {
    event.stopPropagation();
    setSignatureMenuAnchorEl(event.currentTarget);
    setSignatureMenuTask({ task, docId });
  };

  const handleCloseSignatureMenu = () => {
    setSignatureMenuAnchorEl(null);
    setSignatureMenuTask(null);
  };

  // Handle downloading individual document
  const handleDownload = useCallback(
    async (task: Task, docIndex: number = 0) => {
      if (!task.estateDocument?.id || !task.ud_id) {
        toast.error("No document available for download");
        return;
      }

      handleCloseMenu();

      try {
        const documentV2 = task.estateDocument?.documents?.[docIndex];
        
        if (!documentV2?.document_id) {
          toast.error("Document ID not found");
          return;
        }

        const params = {
          ud_id: task.ud_id,
          document_id: documentV2.document_id,
          entity_id: task.estateDocument.id,
          entity: "estate-documents",
        };

        const response: any = await downloadDocument(params);

        let fileUrl = null;
        if (response && typeof response === "string") {
          fileUrl = response;
        } else if (response && response.file_url) {
          fileUrl = response.file_url;
        }

        if (fileUrl) {
          // Fetch the PDF as a blob to force download (cross-origin compatible)
          const pdfResponse = await fetch(fileUrl);
          const blob = await pdfResponse.blob();
          const blobUrl = window.URL.createObjectURL(blob);

          // Trigger download
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download =
            documentV2.document_name ||
            task.estateDocument.name ||
            "document.pdf";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up blob URL
          window.URL.revokeObjectURL(blobUrl);

          toast.success("Download started");
        } else {
          throw new Error("No file URL returned");
        }
      } catch (error) {
        toast.error("Failed to download document");
        console.error("Download error:", error);
      }
    },
    [downloadDocument]
  );

  // Handle marking document as signed/not signed
  const handleToggleSigned = useCallback(
    async (taskData?: { task: Task; docId: string }) => {

      // Use provided taskData or fall back to signatureMenuTask
      const dataSource = taskData || signatureMenuTask;

      if (!dataSource || !userData?.ud_id) {
        return;
      }

      const { task, docId } = dataSource;
      const newSignedState = !signedDocs[docId];

      handleCloseSignatureMenu();

      try {
        const estateDoc = task.estateDocument;
        if (!estateDoc?.documents || estateDoc.documents.length === 0) {
          toast.error("No documents found to update");
          return;
        }

        // Extract the document index from the docId
        const docIndex = getDocIndexFromSubDocId(docId);
        const documentToUpdate = estateDoc.documents[docIndex];

        if (!documentToUpdate?.document_id) {
          toast.error("No document found to update");
          return;
        }

        // Get the correct person's ud_id (document owner, not necessarily the logged-in user)
        const documentOwnerUdId =
          task.people[0]?.ud_id || task.ud_id || userData.ud_id;

        // Call the API to update the document's is_signed status
        await updateDocument(
          {
            ud_id: documentOwnerUdId,
            entity: "documents",
            entity_id: documentToUpdate.document_id,
          },
          { is_signed: newSignedState },
          true
        );

        // Update local state after successful API call
        setSignedDocs((prev) => ({
          ...prev,
          [docId]: newSignedState,
        }));

        if (newSignedState) {
          toast.success(`Document marked as signed`);
        } else {
          toast(`Document marked as not signed`);
        }

        // Refresh households to get updated data
        await refreshHouseholds();
      } catch (error) {
        console.error("Failed to update document signed status:", error);
        toast.error("Failed to update document. Please try again.");
      }
    },
    [signatureMenuTask, signedDocs, userData, updateDocument, refreshHouseholds]
  );

  // Handle uploading signed copy
  const handleUploadSignedCopy = useCallback(
    async (task: Task, docId: string) => {
      handleCloseMenu();

      // Extract the DocumentV2's document_id
      let documentV2Id: string | null = null;

      // Check if this is a document package (trust with multiple subdocuments)
      if (isDocumentPackage(task)) {
        // It's a package - extract the index from the docId
        const index = getDocIndexFromSubDocId(docId);
        const doc = task.estateDocument?.documents?.[index];
        if (doc?.document_id) {
          documentV2Id = doc.document_id;
        }
      } else {
        // Regular single document - get the first document
        const doc = task.estateDocument?.documents?.[0];
        if (doc?.document_id) {
          documentV2Id = doc.document_id;
        }
      }

      if (!documentV2Id) {
        toast.error("Could not find document to upload signed copy");
        return;
      }

      // Extract the person's ud_id from the task (use first person for the document owner)
      const personUdId = task.people[0]?.ud_id || task.ud_id;

      uploadSignedCopyDialog.handleOpen({
        task,
        documentId: documentV2Id,
        personUdId,
      });
    },
    [uploadSignedCopyDialog]
  );

  // Initialize signed docs state from backend data
  useEffect(() => {
    const initialSigned: Record<string, boolean> = {};
    tasks.forEach((task) => {
      const documents = task.estateDocument?.documents;
      if (documents && documents.length > 0) {
        // Check if this is a package (trust with multiple subdocuments)
        if (isDocumentPackage(task)) {
          // For packages, initialize each subdocument's signed status
          documents.forEach((doc, index) => {
            const subDocId = `${task.id}-doc-${index}`;
            initialSigned[subDocId] = doc.is_signed ?? false;
          });
        } else {
          // For regular documents, use first document's is_signed status
          initialSigned[task.id] = documents[0]?.is_signed ?? false;
        }
      }
    });
    setSignedDocs(initialSigned);
  }, [selectedHousehold?.head_of_household?.ud_id, tasks]);

  const createNewEstateDocument = useCallback(
    async (
      taskId: string,
      ud_id: string,
      full_name: string,
      type: EstateDocumentType,
      estateDocs: MinimalEstateDocument[],
      joint_trust: boolean
    ) => {
      try {
        const filteredEstateDocs = estateDocs.filter((d) => d.type === type);

        setLoadingTasks((prev) => ({ ...prev, [taskId]: true }));
        let newEstateDoc = null;
        // here we duplicate the most recent document APPROVED document
        if (filteredEstateDocs?.find((d) => d.status === "APPROVED")) {
          newEstateDoc = await estateDocStore.duplicateEstateDocument(
            ud_id || "",
            filteredEstateDocs?.find((d) => d.status === "APPROVED")?.id || ""
          );
        } else {
          // Get the person's name from the household data
          const hoh = selectedHousehold?.head_of_household;
          const spouse = selectedHousehold?.household_member_roles?.find(
            (m) => m.role === "SPOUSE"
          )?.role_target_user_data;

          // Determine whose name to use based on ud_id
          let personName =
            hoh?.ud_id === ud_id ? hoh?.full_name : spouse?.full_name;
          personName = personName || userData?.full_name || "Update Name";

          // Get spouse name for joint trust
          const spouseName =
            joint_trust && spouse ? spouse.full_name : undefined;

          // Generate proper document name
          const documentName = generateDocumentName(
            type,
            personName,
            joint_trust,
            spouseName
          );

          newEstateDoc = await estateDocStore.handleAddOrEditEstateDocument(
            ud_id || "",
            {
              id: null,
              type: type,
              version: null,
              status: "IN_PROGRESS",
              state_code: null,
              address: null,
              data: null,
              user_name: userData?.full_name || "",
              documents: [],
              name: documentName,
              key_details: null,
              notes: null,
              document_source: "SNUG",
              joint_trust: joint_trust,
            }
          );
        }
        // Note: refreshHouseholds() removed - polling hook will update automatically
        if (type === "FINANCIAL_POWER_OF_ATTORNEY") {
          router.replace(
            paths.dashboard.createFinancialPowerOfAttorneyExpress(
              newEstateDoc?.id || "",
              ud_id || ""
            )
          );
        } else if (type === "HEALTHCARE_DIRECTIVE") {
          router.replace(
            paths.dashboard.createHealthCareDirectiveExpress(
              newEstateDoc?.id || "",
              ud_id || ""
            )
          );
        } else if (type === "TRUST" || type === "WILL") {
          router.replace(
            paths.dashboard.createWillOrTrustExpress(
              newEstateDoc?.id || "",
              ud_id || ""
            )
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingTasks((prev) => ({ ...prev, [taskId]: false }));
      }
    },
    [estateDocStore, router, userData?.full_name, selectedHousehold]
  );

  // Helper function to navigate to the correct document index page for updating
  // The index page will handle the logic of creating new vs. continuing existing documents
  const handleUpdateDocument = useCallback(
    async (task: Task) => {
      // Show dialog for completed documents (skip for assets - navigate directly)
      if (task.status === "COMPLETE" && task.kind !== "assets") {
        setPendingCompletedTask(task);
        setCompletedDialogOpen(true);
        return;
      }

      // Prevent duplicate calls if already loading
      if (loadingTasks[task.id]) return;

      // Navigate to the index page - it already has logic to handle document creation/continuation
      let createNew: boolean = false;
      if (
        task.estateDocument?.status === "IN_PROGRESS" &&
        task.estateDocument?.document_source === "SNUG"
      ) {
        createNew = false;
      } else {
        createNew = true;
      }

      if (createNew && task.kind !== "assets") {
        await createNewEstateDocument(
          task.id,
          task.ud_id,
          "",
          task.type,
          task.filteredEstateDocs,
          task.joint_trust
        );
        return;
      }

      switch (task.kind) {
        case "lwt":
          if (useIndexPaths) {
            router.push(paths.dashboard.lwt(task.ud_id || ""));
          } else {
            router.push(
              paths.dashboard.createWillOrTrustExpress(
                task.estateDocument?.id || "",
                task.ud_id || ""
              )
            );
          }
          break;
        case "fpoa":
          if (useIndexPaths) {
            router.push(paths.dashboard.fpoa(task.ud_id || ""));
          } else {
            router.push(
              paths.dashboard.createFinancialPowerOfAttorneyExpress(
                task.estateDocument?.id || "",
                task.ud_id || ""
              )
            );
          }
          break;
        case "hcd":
          if (useIndexPaths) {
            router.push(paths.dashboard.hcd(task.ud_id || ""));
          } else {
            router.push(
              paths.dashboard.createHealthCareDirectiveExpress(
                task.estateDocument?.id || "",
                task.ud_id || ""
              )
            );
          }
          break;
        case "assets":
          // Navigate to assets page
          if (useIndexPaths) {
            router.push(paths.dashboard.assets(task.ud_id || ""));
          } else {
            router.push(paths.dashboard.assetsExpress(task.ud_id || ""));
          }
          break;
        default:
          router.push(task.path);
      }
    },
    [router, createNewEstateDocument, loadingTasks, useIndexPaths]
  );

  // Completed document dialog handlers
  const handleCompletedDialogClose = () => {
    setCompletedDialogOpen(false);
    setPendingCompletedTask(null);
  };

  const handleEditCompletedDocument = async () => {
    if (!pendingCompletedTask) return;

    setCompletedDialogOpen(false);
    const task = pendingCompletedTask;
    setPendingCompletedTask(null);

    // Navigate to the document - reusing existing navigation logic
    switch (task.kind) {
      case "lwt":
        if (useIndexPaths) {
          router.push(paths.dashboard.lwt(task.ud_id || ""));
        } else {
          router.push(
            paths.dashboard.createWillOrTrustExpress(
              task.estateDocument?.id || "",
              task.ud_id || ""
            )
          );
        }
        break;
      case "fpoa":
        if (useIndexPaths) {
          router.push(paths.dashboard.fpoa(task.ud_id || ""));
        } else {
          router.push(
            paths.dashboard.createFinancialPowerOfAttorneyExpress(
              task.estateDocument?.id || "",
              task.ud_id || ""
            )
          );
        }
        break;
      case "hcd":
        if (useIndexPaths) {
          router.push(paths.dashboard.hcd(task.ud_id || ""));
        } else {
          router.push(
            paths.dashboard.createHealthCareDirectiveExpress(
              task.estateDocument?.id || "",
              task.ud_id || ""
            )
          );
        }
        break;
      case "assets":
        // Navigate to assets page
        if (useIndexPaths) {
          router.push(paths.dashboard.assets(task.ud_id || ""));
        } else {
          router.push(paths.dashboard.assetsExpress(task.ud_id || ""));
        }
        break;
      default:
        router.push(task.path);
    }
  };

  // ---------------------------------------------------------------------------
  // 4. render
  // ---------------------------------------------------------------------------
  const mainContent = (
    <>
      {/* progress bar */}
      {express && (
        <Box sx={{ px: { xs: 0, sm: 3 }, pt: { xs: 0, sm: 3 } }}>
          <LinearProgress variant="determinate" value={completionPct} />
        </Box>
      )}
      <Stack
        spacing={3}
        sx={{
          px: { xs: 0, sm: express ? 3 : 0 },
          pb: { xs: 0, sm: express ? 3 : 0 },
          pt: { xs: 3, sm: 3 },
        }}
      >
        {/* Express Pack Summary Card */}
        <Card
          variant="outlined"
          sx={{ p: 2.5, border: "1px solid #e7e7e7", borderRadius: 1 }}
        >
          <Stack spacing={2}>
            {express && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Essential Documents
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create all the essential documents for the household in under
                  20 minutes.
                </Typography>
              </Box>
            )}

            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ display: { xs: "none", sm: "flex" } }}
              >
                {/* Avatar Group */}
                <AvatarGroup max={3} sx={{ justifyContent: "flex-start" }}>
                  {tasks
                    .map((t) => t.people)
                    .flat()
                    .filter(
                      (person, index, self) =>
                        index ===
                        self.findIndex((p) => p.ud_id === person.ud_id)
                    )
                    .slice(0, 3)
                    .map((person) => (
                      <Avatar
                        key={person.ud_id}
                        {...stringAvatar(person?.full_name || "")}
                      />
                    ))}
                </AvatarGroup>

                {/* Summary Text */}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    {(() => {
                      const count = tasks
                        .map((t) => t.people)
                        .flat()
                        .filter(
                          (person, index, self) =>
                            index ===
                            self.findIndex((p) => p.ud_id === person.ud_id)
                        ).length;
                      return `${count} ${count === 1 ? "person" : "people"}`;
                    })()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • {selectedTasks.length}{" "}
                    {selectedTasks.length === 1 ? "document" : "documents"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • 15-20 min
                  </Typography>
                </Stack>
              </Stack>

              {/* Manage People Button */}
              <Button
                variant="contained"
                color="primary"
                disabled={continueButtonLoading}
                startIcon={
                  continueButtonLoading ? (
                    <CircularProgress size={20} />
                  ) : hasStartedDocuments && !nextDocumentWithPerson ? (
                    <FileDownloadOutlinedIcon />
                  ) : undefined
                }
                onClick={async () => {
                  if (continueButtonLoading) return; // Prevent double-clicks

                  if (nextIncompleteTask) {
                    // Navigate to next incomplete document
                    setContinueButtonLoading(true);
                    try {
                      await handleUpdateDocument(nextIncompleteTask);
                    } finally {
                      setContinueButtonLoading(false);
                    }
                  } else if (hasStartedDocuments) {
                    // All documents complete - download all
                    await handleDownloadAllHouseholdDocuments();
                  }
                }}
                sx={{ width: { xs: "100%", sm: "auto" }, borderRadius: 1 }}
              >
                {hasStartedDocuments && nextDocumentWithPerson
                  ? `Continue next document`
                  : hasStartedDocuments
                  ? "Download all documents"
                  : "Continue next document"}
              </Button>
            </Stack>
          </Stack>
        </Card>

        {/* Free-only upsell: prompt lead-gen users to select a will or trust plan */}
        {selectedHousehold?.head_of_household?.estate_plan_foundation === "free_only" && (
          <Card
            variant="outlined"
            sx={{ border: "1px solid #e7e7e7", borderRadius: 1, p: 2.5 }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <AccountBalanceOutlinedIcon sx={{ color: "text.secondary", fontSize: 22 }} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Add a Will or Trust to your plan
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose the right estate plan for your household.
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="outlined"
                size="small"
                onClick={() => router.push(paths.dashboard.onboarding)}
                sx={{ borderRadius: 1, whiteSpace: "nowrap", alignSelf: { xs: "flex-start", sm: "center" } }}
              >
                Select plan
              </Button>
            </Stack>
          </Card>
        )}

        {/* Joint Documents Card - for documents with multiple people */}
        {jointTasks.length > 0 && (
          <Card
            variant="outlined"
            sx={{ mb: 2, border: "1px solid #e7e7e7", borderRadius: 1 }}
          >
            {/* Joint Header with both people's avatars */}
            <Box
              sx={{
                py: 2,
                px: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <AvatarGroup max={2} sx={{ justifyContent: "flex-start" }}>
                  {jointTasks[0].people.map((person) => (
                    <Avatar
                      key={person.ud_id}
                      {...stringAvatar(person?.full_name || "")}
                      sx={{
                        ...stringAvatar(person?.full_name || "").sx,
                        width: 20,
                        height: 20,
                        fontSize: "0.6em",
                      }}
                    />
                  ))}
                </AvatarGroup>
                <Typography variant="body2">
                  {jointTasks[0].people
                    .map((p) => p.full_name?.split(/\s+/)[0] || "Member")
                    .join(" & ")}
                </Typography>
              </Stack>
            </Box>

            <Divider />

            {/* Joint Document List */}
            <List disablePadding sx={{ px: 0, py: 0 }}>
              {jointTasks.map((task) => {
                const isSelected = selectedDocs[task.id] || false;
                const isComplete = task.status === "COMPLETE";
                // PHASE 3: Determine if row should show download mode UI
                const showDownloadMode = isTaskReadyForDownloadMode(task);
                // PHASE 5: Check if this is a package
                const isPackage = isDocumentPackage(task) && showDownloadMode;

                // Choose icon based on task type
                let DocIcon = DescriptionOutlinedIcon;
                if (task.kind === "lwt") DocIcon = GppGoodOutlinedIcon;
                if (task.kind === "hcd") DocIcon = MedicalServicesOutlinedIcon;
                if (task.kind === "fpoa") DocIcon = RequestQuoteOutlinedIcon;
                if (task.kind === "assets")
                  DocIcon = AccountBalanceOutlinedIcon;

                // Extract just the document type from the title
                let docTitle = task.title;
                if (task.kind === "lwt") {
                  if (task.title.includes("Joint"))
                    docTitle = "Joint Living Trust";
                  else if (task.title.includes("Living Trust"))
                    docTitle = "Living Trust";
                  else if (task.title.includes("Will"))
                    docTitle = "Last Will & Testament";
                  else if (task.title.includes("Choose"))
                    docTitle = "Living Trust";
                  else if (task.title.includes("Upload"))
                    docTitle = "Living Trust";
                } else if (task.kind === "fpoa") {
                  docTitle = "Financial Power of Attorney";
                } else if (task.kind === "hcd") {
                  docTitle = "Health Care Directive";
                } else if (task.kind === "assets") {
                  docTitle = "Asset Planning";
                }

                const isLoading = loadingTasks[task.id] || false;

                // PHASE 5: Render package with sub-documents
                if (isPackage) {
                  const subDocs = getSubDocuments(task);
                  return (
                    <React.Fragment key={task.id}>
                      {/* Package Header Row */}
                      <ListItem
                        sx={{
                          py: 1.5,
                          px: { xs: 2, sm: 3 },
                          pl: { xs: 2, sm: 4 },
                        }}
                      >
                        {/* Icon */}
                        <ListItemAvatar
                          sx={{
                            minWidth: 36,
                            display: { xs: "none", sm: "block" },
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: "white",
                              color: "grey.800",
                              width: 24,
                              height: 24,
                            }}
                          >
                            <DocIcon
                              fontSize="small"
                              sx={{ fontSize: "1.2em" }}
                            />
                          </Avatar>
                        </ListItemAvatar>

                        {/* Title */}
                        <ListItemText
                          primary={
                            <Typography
                              variant="subtitle2"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {task.title === "Joint Revocable Living Trust"
                                ? "Joint Trust Package"
                                : "Trust Package"}
                            </Typography>
                          }
                        />

                        {/* No action buttons for package header */}
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.8}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {subDocs.length} documents in Trust Package
                          </Typography>
                        </Stack>
                      </ListItem>

                      {/* Sub-documents (always visible) */}
                      {subDocs.map((subDoc) => {
                        const subDocId = `${task.id}-${subDoc.id}`;
                        const isSubDocSigned = signedDocs[subDocId] || false;

                        return (
                          <ListItem
                            key={subDocId}
                            sx={{
                              py: 1.5,
                              px: { xs: 2, sm: 3 },
                              pl: { xs: 2, sm: 8 },
                              cursor: "pointer",
                              "&:hover": {
                                bgcolor: "action.hover",
                                borderRadius: 1,
                              },
                            }}
                            onClick={() => {
                              const index = getDocIndexFromSubDocId(subDocId);
                              handlePreview(task, index);
                            }}
                          >
                            {/* Arrow icon for sub-documents */}
                            <ListItemAvatar sx={{ minWidth: 18 }}>
                              <PlayArrowRoundedIcon
                                sx={{
                                  fontSize: "11px",
                                  color: "text.secondary",
                                }}
                              />
                            </ListItemAvatar>

                            {/* Sub-document title */}
                            <ListItemText
                              primary={
                                <Typography
                                  variant="body2"
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {subDoc.title}
                                </Typography>
                              }
                            />

                            {/* Action buttons for sub-documents */}
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.8}
                            >
                              {/* Status chip */}
                              {isSubDocSigned ? (
                                <Chip
                                  label="Signed"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenSignatureMenu(
                                      e as any,
                                      task,
                                      subDocId
                                    );
                                  }}
                                  icon={
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        bgcolor: "success.main",
                                        ml: 0.5,
                                      }}
                                    />
                                  }
                                  sx={{
                                    bgcolor: "#e8f5e9",
                                    color: "black",
                                    fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                                    height: { xs: "20px", sm: "24px" },
                                    cursor: "pointer",
                                    "& .MuiChip-icon": {
                                      marginLeft: "8px",
                                      marginRight: "-2px",
                                    },
                                    "& .MuiChip-label": {
                                      px: { xs: 1, sm: 1.5 },
                                    },
                                  }}
                                />
                              ) : (
                                <Chip
                                  label="Ready to Sign"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenSignatureMenu(
                                      e as any,
                                      task,
                                      subDocId
                                    );
                                  }}
                                  icon={
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        bgcolor: "info.main",
                                        ml: 0.5,
                                      }}
                                    />
                                  }
                                  sx={{
                                    bgcolor: "#e3f2fd",
                                    color: "black",
                                    fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                                    height: { xs: "20px", sm: "24px" },
                                    cursor: "pointer",
                                    "& .MuiChip-icon": {
                                      marginLeft: "8px",
                                      marginRight: "-2px",
                                    },
                                    "& .MuiChip-label": {
                                      px: { xs: 1, sm: 1.5 },
                                    },
                                  }}
                                />
                              )}

                              {/* Action buttons */}
                              <Tooltip title="Preview document">
                                <IconButton
                                  size="small"
                                  color="inherit"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const index =
                                      getDocIndexFromSubDocId(subDocId);
                                    handlePreview(task, index);
                                  }}
                                  sx={{
                                    minWidth: "auto",
                                    display: { xs: "none", sm: "inline-flex" },
                                  }}
                                >
                                  <LaunchRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="More options">
                                <IconButton
                                  size="small"
                                  color="inherit"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenMenu(e as any, task, subDocId);
                                  }}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </ListItem>
                        );
                      })}
                    </React.Fragment>
                  );
                }

                // Render regular (non-package) joint documents
                return (
                  <ListItem
                    key={task.id}
                    sx={{
                      py: 1.5,
                      px: { xs: 2, sm: 3 },
                      pl: { xs: 2, sm: 4 },
                      opacity: isLoading ? 0.6 : (task.status === "PENDING" || task.status === "REVIEWABLE") ? 0.5 : isSelected ? 1 : 0.5,
                      cursor: isLoading
                        ? "wait"
                        : (task.status === "PENDING" || task.status === "REVIEWABLE")
                        ? "default"
                        : isSelected
                        ? "pointer"
                        : "default",
                      pointerEvents: (isLoading || task.status === "PENDING" || task.status === "REVIEWABLE") ? "none" : "auto",
                      "&:hover":
                        isSelected && !isLoading && task.status !== "PENDING" && task.status !== "REVIEWABLE"
                          ? {
                              bgcolor: "action.hover",
                              borderRadius: 1,
                            }
                          : {},
                    }}
                    onClick={async () => {
                      if (isSelected && !isLoading && task.status !== "PENDING" && task.status !== "REVIEWABLE") {
                        // PHASE 3: Preview in download mode, edit in intake mode
                        if (showDownloadMode) {
                          await handlePreview(task);
                        } else {
                          await handleUpdateDocument(task);
                        }
                      }
                    }}
                  >
                    {/* Icon */}
                    <ListItemAvatar
                      sx={{
                        minWidth: 36,
                        display: { xs: "none", sm: "block" },
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "white",
                          color: "grey.800",
                          width: 24,
                          height: 24,
                        }}
                      >
                        <DocIcon fontSize="small" sx={{ fontSize: "1.2em" }} />
                      </Avatar>
                    </ListItemAvatar>

                    {/* Title and Badge */}
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {docTitle}
                          </Typography>
                          {task.kind === "hcd" && (
                            <Tooltip
                              title="Health Care Directives include a Medical Power of Attorney, HIPAA Authorization, and Living Will or Advance Directive."
                              placement="top"
                            >
                              <InfoOutlinedIcon
                                fontSize="small"
                                sx={{
                                  fontSize: 14,
                                  display: { xs: "none", sm: "block" },
                                }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      }
                    />

                    {/* Toggle and Action */}
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {/* Loading indicator */}
                      {isLoading && <CircularProgress size={16} />}

                      {/* PHASE 3: Download mode UI - show when complete */}
                      {!isLoading && showDownloadMode && isSelected && (
                        <>
                          {/* Signed status chip (clickable) */}
                          <Chip
                            label={
                              signedDocs[task.id] ? "Signed" : "Ready to Sign"
                            }
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenSignatureMenu(e as any, task, task.id);
                            }}
                            icon={
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: signedDocs[task.id]
                                    ? "success.main"
                                    : "info.main",
                                  ml: 0.5,
                                }}
                              />
                            }
                            sx={{
                              bgcolor: signedDocs[task.id]
                                ? "#e8f5e9"
                                : "#e3f2fd",
                              color: "black",
                              cursor: "pointer",
                              fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                              height: { xs: "20px", sm: "24px" },
                              "& .MuiChip-icon": {
                                marginLeft: "8px",
                                marginRight: "-2px",
                              },
                              "& .MuiChip-label": {
                                px: { xs: 1, sm: 1.5 },
                              },
                            }}
                          />

                          {/* Preview button */}
                          <Tooltip title="Preview document">
                            <IconButton
                              size="small"
                              color="inherit"
                              onClick={async (e) => {
                                e.stopPropagation();
                                await handlePreview(task);
                              }}
                              sx={{
                                minWidth: "auto",
                                display: { xs: "none", sm: "inline-flex" },
                              }}
                            >
                              <LaunchRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* More options menu */}
                          <Tooltip title="More options">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenMenu(e as any, task);
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      {/* Intake mode UI - show "Complete" chip when complete but keep in intake */}
                      {!isLoading &&
                        task.status === "COMPLETE" &&
                        !showDownloadMode && (
                          <>
                            <Chip
                              label={task.label || "Complete"}
                              size="small"
                              icon={
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    bgcolor: "success.main",
                                    ml: 0.5,
                                  }}
                                />
                              }
                              sx={{
                                bgcolor: "#e8f5e9",
                                color: "black",
                                fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                                height: { xs: "20px", sm: "24px" },
                                "& .MuiChip-label": {
                                  px: { xs: 1, sm: 1.5 },
                                },
                                "& .MuiChip-icon": {
                                  marginLeft: "8px",
                                  marginRight: "-2px",
                                },
                              }}
                            />
                          </>
                        )}

                      {!isLoading &&
                        task.status === "IN_PROGRESS" &&
                        isSelected && (
                          <Chip
                            label="In Progress"
                            size="small"
                            icon={
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: "info.main",
                                  ml: 0.5,
                                }}
                              />
                            }
                            sx={{
                              bgcolor: "#e3f2fd",
                              color: "black",
                              fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                              height: { xs: "20px", sm: "24px" },
                              "& .MuiChip-label": {
                                px: { xs: 1, sm: 1.5 },
                              },
                              "& .MuiChip-icon": {
                                marginLeft: "8px",
                                marginRight: "-2px",
                              },
                            }}
                          />
                        )}

                      {!isLoading &&
                        (task.status === "PENDING" ||
                          task.status === "REVIEWABLE") &&
                        isSelected && (
                          <Chip
                            label="Generating"
                            size="small"
                            icon={
                              <CircularProgress
                                size={10}
                                thickness={5}
                                sx={{
                                  color: "success.main",
                                  ml: 0.5,
                                }}
                              />
                            }
                            sx={{
                              bgcolor: "#e8f5e9",
                              color: "black",
                              fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                              height: { xs: "20px", sm: "24px" },
                              "& .MuiChip-label": {
                                px: { xs: 1, sm: 1.5 },
                              },
                              "& .MuiChip-icon": {
                                marginLeft: "8px",
                                marginRight: "-2px",
                              },
                            }}
                          />
                        )}

                      {!isLoading &&
                        task.status === "NOT_STARTED" &&
                        isSelected && (
                          <Chip
                            label="Not Started"
                            size="small"
                            icon={
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: "grey.500",
                                  ml: 0.5,
                                }}
                              />
                            }
                            sx={{
                              bgcolor: "#f5f5f5",
                              color: "text.primary",
                              fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                              height: { xs: "20px", sm: "24px" },
                              "& .MuiChip-label": {
                                px: { xs: 1, sm: 1.5 },
                              },
                              "& .MuiChip-icon": {
                                marginLeft: "8px",
                                marginRight: "-2px",
                              },
                            }}
                          />
                        )}

                      {!isLoading &&
                        (task.status === "IN_PROGRESS" ||
                          task.status === "PENDING" ||
                          task.status === "REVIEWABLE" ||
                          task.status === "NOT_STARTED") &&
                        !isSelected && (
                          <Typography
                            variant="body2"
                            color="text.disabled"
                            sx={{ minWidth: 100, textAlign: "center" }}
                          >
                            Removed
                          </Typography>
                        )}
                      {!isComplete &&
                        !(
                          task.status === "PENDING" ||
                          task.status === "REVIEWABLE"
                        ) &&
                        (task.kind === "fpoa" || task.kind === "hcd") && (
                          <Tooltip
                            title={
                              isSelected
                                ? "Remove from checklist"
                                : "Add to checklist"
                            }
                            placement="top"
                          >
                            <IconButton
                              size="small"
                              disabled={loadingSwitches[task.id]}
                              onClick={async (e) => {
                                e.stopPropagation();

                                // If removing (trash can clicked), show confirmation dialog
                                if (isSelected) {
                                  setPendingRemoveTask(task);
                                  setRemoveDocDialogOpen(true);
                                  return;
                                }

                                // If adding (plus clicked), proceed immediately
                                const newValue = true;

                                // Update local state immediately for responsive UI
                                setSelectedDocs((prev) => ({
                                  ...prev,
                                  [task.id]: newValue,
                                }));

                                // Only update DB for FPOA and HCD tasks (not LWT)
                                if (
                                  task.kind === "fpoa" ||
                                  task.kind === "hcd"
                                ) {
                                  try {
                                    // Set loading state
                                    setLoadingSwitches((prev) => ({
                                      ...prev,
                                      [task.id]: true,
                                    }));

                                    // Get the person's UserData
                                    const person = task.people[0];

                                    if (person?.ud_id && userData?.ud_id) {
                                      // Determine which field to update
                                      const field =
                                        task.kind === "fpoa"
                                          ? "include_fpoa"
                                          : "include_hcd";

                                      // Call API to update UserData
                                      await updateUserData(
                                        {
                                          ud_id: userData.ud_id,
                                          entity: "role-target-user-data",
                                          entity_id: person.ud_id,
                                        },
                                        { [field]: newValue },
                                        true // partial update
                                      );

                                      // Note: refreshHouseholds() removed - polling hook will update automatically

                                      toast.success(
                                        "Added document to checklist"
                                      );
                                    } else {
                                      toast.success(
                                        "Added document to checklist"
                                      );
                                    }
                                  } catch (error) {
                                    console.error(
                                      "Failed to update document preference:",
                                      error
                                    );
                                    // Revert local state on error
                                    setSelectedDocs((prev) => ({
                                      ...prev,
                                      [task.id]: false,
                                    }));
                                    toast.error(
                                      "Failed to save preference. Please try again."
                                    );
                                  } finally {
                                    // Clear loading state
                                    setLoadingSwitches((prev) => ({
                                      ...prev,
                                      [task.id]: false,
                                    }));
                                  }
                                } else {
                                  toast.success("Added document to checklist");
                                }
                              }}
                              sx={{ color: "text.secondary" }}
                            >
                              {isSelected ? (
                                <DeleteOutlineIcon fontSize="small" />
                              ) : (
                                <AddRoundedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                    </Stack>
                  </ListItem>
                );
              })}
            </List>
          </Card>
        )}

        {/* Person-based Document Organization - for individual documents */}
        {Object.entries(tasksByPerson).map(
          ([personId, { person, tasks: personTasks }]) => {
            const fullName = person.full_name || "Member";

            return (
              <Card
                key={personId}
                variant="outlined"
                sx={{ mb: 2, border: "1px solid #e7e7e7", borderRadius: 1 }}
              >
                {/* Person Header */}
                <Box
                  sx={{
                    py: 2,
                    px: 2.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar
                      {...stringAvatar(person?.full_name || "")}
                      sx={{
                        ...stringAvatar(person?.full_name || "").sx,
                        width: 20,
                        height: 20,
                        fontSize: "0.6em",
                      }}
                    />
                    <Typography variant="body2">{fullName}</Typography>
                  </Stack>
                </Box>

                <Divider />

                {/* Document List */}
                <List disablePadding sx={{ px: 0, py: 0 }}>
                  {personTasks.map((task) => {
                    const isSelected = selectedDocs[task.id] || false;
                    const isComplete = task.status === "COMPLETE";
                    // PHASE 3: Determine if row should show download mode UI
                    const showDownloadMode = isTaskReadyForDownloadMode(task);

                    // Choose icon based on task type
                    let DocIcon = DescriptionOutlinedIcon;
                    if (task.kind === "lwt") DocIcon = GppGoodOutlinedIcon;
                    if (task.kind === "hcd")
                      DocIcon = MedicalServicesOutlinedIcon;
                    if (task.kind === "fpoa")
                      DocIcon = RequestQuoteOutlinedIcon;
                    if (task.kind === "assets")
                      DocIcon = AccountBalanceOutlinedIcon;

                    // Extract just the document type from the title
                    let docTitle = task.title;
                    if (task.kind === "lwt") {
                      if (task.title.includes("Joint"))
                        docTitle = "Living Trust";
                      else if (task.title.includes("Living Trust"))
                        docTitle = "Living Trust";
                      else if (task.title.includes("Will"))
                        docTitle = "Last Will & Testament";
                      else if (task.title.includes("Choose"))
                        docTitle = "Living Trust";
                      else if (task.title.includes("Upload"))
                        docTitle = "Living Trust";
                    } else if (task.kind === "fpoa") {
                      docTitle = "Financial Power of Attorney";
                    } else if (task.kind === "hcd") {
                      docTitle = "Health Care Directive";
                    } else if (task.kind === "assets") {
                      docTitle = "Asset Planning";
                    }

                    const isLoading = loadingTasks[task.id] || false;
                    // PHASE 5: Check if this is a package
                    const isPackage =
                      isDocumentPackage(task) && showDownloadMode;

                    // PHASE 5: Render package with sub-documents
                    if (isPackage) {
                      const subDocs = getSubDocuments(task);
                      return (
                        <React.Fragment key={task.id}>
                          {/* Package Header Row */}
                          <ListItem
                            sx={{
                              py: 1.5,
                              px: { xs: 2, sm: 3 },
                              pl: { xs: 2, sm: 4 },
                            }}
                          >
                            {/* Icon */}
                            <ListItemAvatar
                              sx={{
                                minWidth: 36,
                                display: { xs: "none", sm: "block" },
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: "white",
                                  color: "grey.800",
                                  width: 24,
                                  height: 24,
                                }}
                              >
                                <DocIcon
                                  fontSize="small"
                                  sx={{ fontSize: "1.2em" }}
                                />
                              </Avatar>
                            </ListItemAvatar>

                            {/* Title */}
                            <ListItemText
                              primary={
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Trust Package
                                </Typography>
                              }
                            />

                            {/* No action buttons for package header */}
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.8}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {subDocs.length} documents in Trust Package
                              </Typography>
                            </Stack>
                          </ListItem>

                          {/* Sub-documents (always visible) */}
                          {subDocs.map((subDoc) => {
                            const subDocId = `${task.id}-${subDoc.id}`;
                            const isSubDocSigned =
                              signedDocs[subDocId] || false;

                            return (
                              <ListItem
                                key={subDocId}
                                sx={{
                                  py: 1.5,
                                  px: { xs: 2, sm: 3 },
                                  pl: { xs: 2, sm: 8 },
                                  cursor: "pointer",
                                  "&:hover": {
                                    bgcolor: "action.hover",
                                    borderRadius: 1,
                                  },
                                }}
                                onClick={() => {
                                  const index =
                                    getDocIndexFromSubDocId(subDocId);
                                  handlePreview(task, index);
                                }}
                              >
                                {/* Arrow icon for sub-documents */}
                                <ListItemAvatar sx={{ minWidth: 18 }}>
                                  <PlayArrowRoundedIcon
                                    sx={{
                                      fontSize: "11px",
                                      color: "text.secondary",
                                    }}
                                  />
                                </ListItemAvatar>

                                {/* Sub-document title */}
                                <ListItemText
                                  primary={
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {subDoc.title}
                                    </Typography>
                                  }
                                />

                                {/* Action buttons for sub-documents */}
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.8}
                                >
                                  {/* Status chip */}
                                  {isSubDocSigned ? (
                                    <Chip
                                      label="Signed"
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenSignatureMenu(
                                          e as any,
                                          task,
                                          subDocId
                                        );
                                      }}
                                      icon={
                                        <Box
                                          sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            bgcolor: "success.main",
                                            ml: 0.5,
                                          }}
                                        />
                                      }
                                      sx={{
                                        bgcolor: "#e8f5e9",
                                        color: "black",
                                        fontSize: {
                                          xs: "0.7rem",
                                          sm: "0.8125rem",
                                        },
                                        height: { xs: "20px", sm: "24px" },
                                        cursor: "pointer",
                                        "& .MuiChip-icon": {
                                          marginLeft: "8px",
                                          marginRight: "-2px",
                                        },
                                        "& .MuiChip-label": {
                                          px: { xs: 1, sm: 1.5 },
                                        },
                                      }}
                                    />
                                  ) : (
                                    <Chip
                                      label="Ready to Sign"
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenSignatureMenu(
                                          e as any,
                                          task,
                                          subDocId
                                        );
                                      }}
                                      icon={
                                        <Box
                                          sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            bgcolor: "info.main",
                                            ml: 0.5,
                                          }}
                                        />
                                      }
                                      sx={{
                                        bgcolor: "#e3f2fd",
                                        color: "black",
                                        fontSize: {
                                          xs: "0.7rem",
                                          sm: "0.8125rem",
                                        },
                                        height: { xs: "20px", sm: "24px" },
                                        cursor: "pointer",
                                        "& .MuiChip-icon": {
                                          marginLeft: "8px",
                                          marginRight: "-2px",
                                        },
                                        "& .MuiChip-label": {
                                          px: { xs: 1, sm: 1.5 },
                                        },
                                      }}
                                    />
                                  )}

                                  {/* Action buttons */}
                                  <Tooltip title="Preview document">
                                    <IconButton
                                      size="small"
                                      color="inherit"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const index =
                                          getDocIndexFromSubDocId(subDocId);
                                        handlePreview(task, index);
                                      }}
                                      sx={{
                                        minWidth: "auto",
                                        display: {
                                          xs: "none",
                                          sm: "inline-flex",
                                        },
                                      }}
                                    >
                                      <LaunchRoundedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="More options">
                                    <IconButton
                                      size="small"
                                      color="inherit"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenMenu(
                                          e as any,
                                          task,
                                          subDocId
                                        );
                                      }}
                                    >
                                      <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </ListItem>
                            );
                          })}
                        </React.Fragment>
                      );
                    }

                    // Render regular (non-package) documents
                    return (
                      <ListItem
                        key={task.id}
                        sx={{
                          py: 1.5,
                          px: { xs: 2, sm: 3 },
                          pl: { xs: 2, sm: 4 },
                          opacity: isLoading ? 0.6 : (task.status === "PENDING" || task.status === "REVIEWABLE") ? 0.5 : isSelected ? 1 : 0.5,
                          cursor: isLoading
                            ? "wait"
                            : (task.status === "PENDING" || task.status === "REVIEWABLE")
                            ? "default"
                            : isSelected
                            ? "pointer"
                            : "default",
                          pointerEvents: (isLoading || task.status === "PENDING" || task.status === "REVIEWABLE") ? "none" : "auto",
                          "&:hover":
                            isSelected && !isLoading && task.status !== "PENDING" && task.status !== "REVIEWABLE"
                              ? {
                                  bgcolor: "action.hover",
                                  borderRadius: 1,
                                }
                              : {},
                        }}
                        onClick={async () => {
                          if (isSelected && !isLoading && task.status !== "PENDING" && task.status !== "REVIEWABLE") {
                            // PHASE 3: Preview in download mode, edit in intake mode
                            if (showDownloadMode) {
                              await handlePreview(task);
                            } else {
                              await handleUpdateDocument(task);
                            }
                          }
                        }}
                      >
                        {/* Icon */}
                        <ListItemAvatar
                          sx={{
                            minWidth: 36,
                            display: { xs: "none", sm: "block" },
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: "white",
                              color: "grey.800",
                              width: 24,
                              height: 24,
                            }}
                          >
                            <DocIcon
                              fontSize="small"
                              sx={{ fontSize: "1.2em" }}
                            />
                          </Avatar>
                        </ListItemAvatar>

                        {/* Title and Badge */}
                        <ListItemText
                          primary={
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {docTitle}
                              </Typography>
                              {task.kind === "hcd" && (
                                <Tooltip
                                  title="Health Care Directives include a Medical Power of Attorney, HIPAA Authorization, and Living Will or Advance Directive."
                                  placement="top"
                                >
                                  <InfoOutlinedIcon
                                    fontSize="small"
                                    sx={{
                                      fontSize: 14,
                                      display: { xs: "none", sm: "block" },
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          }
                        />

                        {/* Toggle and Action */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {/* Loading indicator */}
                          {isLoading && <CircularProgress size={16} />}

                          {/* PHASE 3: Download mode UI - show when complete */}
                          {!isLoading && showDownloadMode && isSelected && (
                            <>
                              {/* Signed status chip (clickable) */}
                              <Chip
                                label={
                                  signedDocs[task.id]
                                    ? "Signed"
                                    : "Ready to Sign"
                                }
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenSignatureMenu(
                                    e as any,
                                    task,
                                    task.id
                                  );
                                }}
                                icon={
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      bgcolor: signedDocs[task.id]
                                        ? "success.main"
                                        : "info.main",
                                      ml: 0.5,
                                    }}
                                  />
                                }
                                sx={{
                                  bgcolor: signedDocs[task.id]
                                    ? "#e8f5e9"
                                    : "#e3f2fd",
                                  color: "black",
                                  cursor: "pointer",
                                  fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                                  height: { xs: "20px", sm: "24px" },
                                  "& .MuiChip-icon": {
                                    marginLeft: "8px",
                                    marginRight: "-2px",
                                  },
                                  "& .MuiChip-label": {
                                    px: { xs: 1, sm: 1.5 },
                                  },
                                }}
                              />

                              {/* Preview button */}
                              <Tooltip title="Preview document">
                                <IconButton
                                  size="small"
                                  color="inherit"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    await handlePreview(task);
                                  }}
                                  sx={{
                                    minWidth: "auto",
                                    display: { xs: "none", sm: "inline-flex" },
                                  }}
                                >
                                  <LaunchRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              {/* More options menu */}
                              <Tooltip title="More options">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenMenu(e as any, task);
                                  }}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}

                          {/* Intake mode UI - show "Complete" chip when complete but keep in intake */}
                          {!isLoading &&
                            task.status === "COMPLETE" &&
                            !showDownloadMode && (
                              <>
                                <Chip
                                  label={task.label || "Complete"}
                                  size="small"
                                  icon={
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        bgcolor: "success.main",
                                        ml: 0.5,
                                      }}
                                    />
                                  }
                                  sx={{
                                    bgcolor: "#e8f5e9",
                                    color: "black",
                                    fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                                    height: { xs: "20px", sm: "24px" },
                                    "& .MuiChip-label": {
                                      px: { xs: 1, sm: 1.5 },
                                    },
                                    "& .MuiChip-icon": {
                                      marginLeft: "8px",
                                      marginRight: "-2px",
                                    },
                                  }}
                                />
                              </>
                            )}

                          {!isLoading &&
                            task.status === "IN_PROGRESS" &&
                            isSelected && (
                              <Chip
                                label="In Progress"
                                size="small"
                                icon={
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      bgcolor: "info.main",
                                      ml: 0.5,
                                    }}
                                  />
                                }
                                sx={{
                                  bgcolor: "#e3f2fd",
                                  color: "black",
                                  fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                                  height: { xs: "20px", sm: "24px" },
                                  "& .MuiChip-label": {
                                    px: { xs: 1, sm: 1.5 },
                                  },
                                  "& .MuiChip-icon": {
                                    marginLeft: "8px",
                                    marginRight: "-2px",
                                  },
                                }}
                              />
                            )}

                          {!isLoading &&
                            (task.status === "PENDING" ||
                              task.status === "REVIEWABLE") &&
                            isSelected && (
                              <Chip
                                label="Generating"
                                size="small"
                                icon={
                                  <CircularProgress
                                    size={10}
                                    thickness={5}
                                    sx={{
                                      color: "success.main",
                                      ml: 0.5,
                                    }}
                                  />
                                }
                                sx={{
                                  bgcolor: "#e8f5e9",
                                  color: "black",
                                  fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                                  height: { xs: "20px", sm: "24px" },
                                  "& .MuiChip-label": {
                                    px: { xs: 1, sm: 1.5 },
                                  },
                                  "& .MuiChip-icon": {
                                    marginLeft: "8px",
                                    marginRight: "-2px",
                                  },
                                }}
                              />
                            )}

                          {!isLoading &&
                            task.status === "NOT_STARTED" &&
                            isSelected && (
                              <Chip
                                label="Not Started"
                                size="small"
                                icon={
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      bgcolor: "grey.500",
                                      ml: 0.5,
                                    }}
                                  />
                                }
                                sx={{
                                  bgcolor: "#f5f5f5",
                                  color: "text.primary",
                                  fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                                  height: { xs: "20px", sm: "24px" },
                                  "& .MuiChip-label": {
                                    px: { xs: 1, sm: 1.5 },
                                  },
                                  "& .MuiChip-icon": {
                                    marginLeft: "8px",
                                    marginRight: "-2px",
                                  },
                                }}
                              />
                            )}

                          {!isLoading &&
                            (task.status === "IN_PROGRESS" ||
                              task.status === "PENDING" ||
                              task.status === "REVIEWABLE" ||
                              task.status === "NOT_STARTED") &&
                            !isSelected && (
                              <Typography
                                variant="body2"
                                color="text.disabled"
                                sx={{ minWidth: 100, textAlign: "center" }}
                              >
                                Removed
                              </Typography>
                            )}
                          {!isComplete &&
                            !(
                              task.status === "PENDING" ||
                              task.status === "REVIEWABLE"
                            ) &&
                            (task.kind === "fpoa" || task.kind === "hcd") && (
                              <Tooltip
                                title={
                                  isSelected
                                    ? "Remove from checklist"
                                    : "Add to checklist"
                                }
                                placement="top"
                              >
                                <IconButton
                                  size="small"
                                  disabled={loadingSwitches[task.id]}
                                  onClick={async (e) => {
                                    e.stopPropagation();

                                    // If removing (trash can clicked), show confirmation dialog
                                    if (isSelected) {
                                      setPendingRemoveTask(task);
                                      setRemoveDocDialogOpen(true);
                                      return;
                                    }

                                    // If adding (plus clicked), proceed immediately
                                    const newValue = true;

                                    // Update local state immediately for responsive UI
                                    setSelectedDocs((prev) => ({
                                      ...prev,
                                      [task.id]: newValue,
                                    }));

                                    // Only update DB for FPOA and HCD tasks (not LWT)
                                    if (
                                      task.kind === "fpoa" ||
                                      task.kind === "hcd"
                                    ) {
                                      try {
                                        // Set loading state
                                        setLoadingSwitches((prev) => ({
                                          ...prev,
                                          [task.id]: true,
                                        }));

                                        // Get the person's UserData
                                        const person = task.people[0];

                                        if (person?.ud_id && userData?.ud_id) {
                                          // Determine which field to update
                                          const field =
                                            task.kind === "fpoa"
                                              ? "include_fpoa"
                                              : "include_hcd";

                                          // Call API to update UserData
                                          await updateUserData(
                                            {
                                              ud_id: userData.ud_id,
                                              entity: "role-target-user-data",
                                              entity_id: person.ud_id,
                                            },
                                            { [field]: newValue },
                                            true // partial update
                                          );

                                          // Note: refreshHouseholds() removed - polling hook will update automatically

                                          toast.success(
                                            "Added document to checklist"
                                          );
                                        } else {
                                          toast.success(
                                            "Added document to checklist"
                                          );
                                        }
                                      } catch (error) {
                                        console.error(
                                          "Failed to update document preference:",
                                          error
                                        );
                                        // Revert local state on error
                                        setSelectedDocs((prev) => ({
                                          ...prev,
                                          [task.id]: false,
                                        }));
                                        toast.error(
                                          "Failed to save preference. Please try again."
                                        );
                                      } finally {
                                        // Clear loading state
                                        setLoadingSwitches((prev) => ({
                                          ...prev,
                                          [task.id]: false,
                                        }));
                                      }
                                    } else {
                                      toast.success(
                                        "Added document to checklist"
                                      );
                                    }
                                  }}
                                  sx={{ color: "text.secondary" }}
                                >
                                  {isSelected ? (
                                    <DeleteOutlineIcon fontSize="small" />
                                  ) : (
                                    <AddRoundedIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                        </Stack>
                      </ListItem>
                    );
                  })}
                </List>
              </Card>
            );
          }
        )}
      </Stack>
    </>
  );

  return (
    <>
      {express ? (
        <Card
          variant="outlined"
          sx={{
            bgcolor: "#FAF9F8",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            border: { xs: "none", sm: "1px solid #e7e7e7" },
            borderRadius: { xs: 0, sm: 1 },
          }}
        >
          {mainContent}
        </Card>
      ) : (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {mainContent}
        </Box>
      )}

      {/* Remove Document Confirmation Dialog */}
      <Dialog
        open={removeDocDialogOpen}
        onClose={() => {
          setRemoveDocDialogOpen(false);
          setPendingRemoveTask(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">Remove this document?</Typography>
            <IconButton
              aria-label="close"
              onClick={() => {
                setRemoveDocDialogOpen(false);
                setPendingRemoveTask(null);
              }}
              sx={{ color: "grey.500" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Are you sure you want to remove this document from your checklist?
            You'll be able to add it later if you change your mind.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="text"
            color="inherit"
            onClick={() => {
              setRemoveDocDialogOpen(false);
              setPendingRemoveTask(null);
            }}
          >
            No, nevermind
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ borderRadius: 1 }}
            onClick={handleConfirmRemoveDoc}
          >
            Yes, remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Completed Document Dialog */}
      <Dialog
        open={completedDialogOpen}
        onClose={handleCompletedDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">Document Created</Typography>
            <IconButton
              aria-label="close"
              onClick={handleCompletedDialogClose}
              sx={{ color: "grey.500" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Your document has been created. You'll be able to preview and
            download it after all drafts are complete.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: "start" }}>
          <Button
            variant="contained"
            color="primary"
            sx={{ borderRadius: 1 }}
            onClick={handleCompletedDialogClose}
          >
            Go back to checklist
          </Button>
          <Button
            variant="text"
            color="inherit"
            onClick={handleEditCompletedDocument}
          >
            Make changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* PHASE 1: Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            backgroundColor: "#FAF9F8",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Left side: Document info with icon and metadata */}
          <Stack spacing={1.5}>
            {/* Row 1: Icon + Title */}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="h5">{previewTitle}</Typography>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: -1, mb: 1.5, display: "block" }}
            >
              {formatLastUpdated(
                allPreviewableDocuments[currentPreviewIndex]?.parentTask
                  ?.updatedAt
              )}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                sx={{ borderRadius: 100 }}
                startIcon={<TaskOutlinedIcon />}
                onClick={() => {
                  const currentDoc =
                    allPreviewableDocuments[currentPreviewIndex];
                  if (currentDoc) {
                    // Pass the task and document ID to the handler
                    handleToggleSigned({
                      task: currentDoc.parentTask,
                      docId: currentDoc.id,
                    });
                  }
                }}
              >
                {allPreviewableDocuments[currentPreviewIndex] &&
                signedDocs[allPreviewableDocuments[currentPreviewIndex].id]
                  ? "Mark as Not Signed"
                  : "Mark as Signed"}
              </Button>
              {allowClientDocUpdates && (
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  sx={{ borderRadius: 100 }}
                  disabled={makeChangesLoading}
                  startIcon={
                    makeChangesLoading ? (
                      <CircularProgress size={16} />
                    ) : undefined
                  }
                  onClick={handleMakeChanges}
                >
                  Make Changes
                </Button>
              )}
            </Stack>
          </Stack>

          {/* Navigation controls in header */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {allPreviewableDocuments.length > 1 && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  color="inherit"
                  onClick={handlePreviousDocument}
                  disabled={currentPreviewIndex === 0}
                  sx={{ borderRadius: "100px" }}
                  startIcon={<ArrowBackIcon />}
                >
                  Previous
                </Button>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mx: 1 }}
                >
                  {currentPreviewIndex + 1} of {allPreviewableDocuments.length}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleNextDocument}
                  disabled={
                    currentPreviewIndex === allPreviewableDocuments.length - 1
                  }
                  sx={{ borderRadius: "100px" }}
                  endIcon={<ArrowForwardIcon />}
                >
                  Next
                </Button>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              </>
            )}

            <IconButton
              aria-label="close"
              onClick={handleClosePreview}
              size="small"
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {previewLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : previewUrl ? (
            <iframe
              src={previewUrl}
              width="100%"
              height="100%"
              style={{ border: "none" }}
              title="Document Preview"
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography color="text.secondary">
                No preview available
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* PHASE 1: Context Menu for More Options */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem
          onClick={() => {
            if (!menuTask) return;
            const index = getDocIndexFromSubDocId(menuTask.docId);
            handlePreview(menuTask.task, index);
          }}
        >
          Preview
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menuTask) return;
            const index = getDocIndexFromSubDocId(menuTask.docId);
            handleDownload(menuTask.task, index);
          }}
        >
          Download
        </MenuItem>
      </Menu>

      {/* PHASE 1: Signature Status Menu */}
      <Menu
        anchorEl={signatureMenuAnchorEl}
        open={Boolean(signatureMenuAnchorEl)}
        onClose={handleCloseSignatureMenu}
      >
        <MenuItem onClick={() => handleToggleSigned()}>
          {signatureMenuTask && signedDocs[signatureMenuTask.docId]
            ? "Mark as Not Signed"
            : "Mark as Signed"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (signatureMenuTask) {
              handleUploadSignedCopy(
                signatureMenuTask.task,
                signatureMenuTask.docId
              );
            }
          }}
        >
          Upload Signed Copy
        </MenuItem>
      </Menu>

      {/* PHASE 1: Upload Signed Copy Dialog */}
      <UploadSignedCopyDialog
        onCancel={() => {
          handleEstateDocumentFilesRemoveAll();
          uploadSignedCopyDialog.handleClose();
        }}
        open={uploadSignedCopyDialog.open}
        mostRecentEstateDoc={
          uploadSignedCopyDialog.data?.task?.estateDocument ?? null
        }
        documentId={uploadSignedCopyDialog.data?.documentId ?? null}
        personUdId={uploadSignedCopyDialog.data?.personUdId ?? null}
        estateDocumentSaving={estateDocumentSaving}
        estateDocFilesToUpload={estateDocFilesToUpload}
        handleEstateDocumentFilesDrop={handleEstateDocumentFilesDrop}
        handleEstateDocumentFileRemove={handleEstateDocumentFileRemove}
        handleEstateDocumentFilesRemoveAll={handleEstateDocumentFilesRemoveAll}
        handleEdiEstateDocumentPatchOnly={handleEdiEstateDocumentPatchOnly}
        refreshHouseholds={refreshHouseholds}
        handleUploadSignedCopy={handleUploadSignedCopyWithPersonUdId}
        onUploadSuccess={(documentId) => {
          // Update local signed docs state to show "signed" pill immediately
          setSignedDocs((prev) => ({
            ...prev,
            [documentId]: true,
          }));
          toast.success("Signed copy uploaded successfully");
        }}
      />
    </>
  );
};

export { HouseholdChecklistExpress };
